import {
  buildLegalCosmeticArtPool,
  computeDeckColorIdentity,
  GLOBAL_DISTINCT_VARIANTS,
  normalizeFocalPoint,
  resolveDistinctTablePalettes,
  resolveValidatedCardArtUrl,
} from './cosmetics.ts';
import { createTableSettingsFromPreset } from './presets.ts';
import { validateAlterRegistration } from './paperVision.ts';
import { classifyBattlefieldRow, extractTokensFromDeck, GAME_CHANGER_NAMES } from './starterDecks.ts';
import { dealCubePoolsToSeats } from './importers.ts';
import type {
  AuthProviderId,
  BattlefieldRow,
  CardDefinition,
  CardInstance,
  FormatId,
  GameEvent,
  GameIntent,
  GamePhase,
  GameState,
  ImportedDeckPayload,
  ManaPoolColor,
  PaperSeatConfig,
  PaperVideoMarker,
  PlayerSeat,
  TableSettings,
  ZoneId,
} from './types.ts';
import { GAME_PHASES } from './types.ts';

export interface RngProvider {
  /** Cryptographically secure integer in [0, maxExclusive) */
  nextInt(maxExclusive: number): number;
  now(): number;
}

export interface IntentActor {
  accountId: string;
  seatId: string | null;
  isHost: boolean;
  isJudge: boolean;
  displayName: string;
  publicId: string;
}

export interface IntentResult {
  ok: boolean;
  error?: string;
  openFlags?: GameState['flags'];
  state: GameState;
  emittedEvents: GameEvent[];
  /** When UNDO / ROLLBACK truncates effective history, replacement canonical log */
  replacedEventLog?: GameEvent[];
}

const SEAT_COLORS = [
  '#f59e0b',
  '#38bdf8',
  '#a855f7',
  '#10b981',
  '#f43f5e',
  '#eab308',
  '#06b6d4',
  '#ec4899',
];

function emptyZonesForSeat(): Record<ZoneId, string[]> {
  return {
    library: [],
    hand: [],
    battlefield: [],
    graveyard: [],
    exile: [],
    command: [],
    sideboard: [],
    stack: [],
    revealed: [],
  };
}

function emptyManaPool(): Record<ManaPoolColor, number> {
  return { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
}

function defaultPaperSeatConfig(): PaperSeatConfig {
  return {
    enabled: false,
    cameraMode: 'off',
    consentAccepted: false,
    aspectRatio: '16:9',
    preferredCodec: 'vp9',
    lowBandwidthFps: 1,
    tableOnlyCrop: { top: 0, right: 0, bottom: 0, left: 0 },
    privacyBlur: false,
    videoMarkers: [],
    scannedCardsSeen: [],
    registeredAlters: [],
  };
}

export function createEmptySeat(seatIndex: number, startingLife: number, teamMode = false): PlayerSeat {
  const seatId = `seat_${seatIndex + 1}`;
  const defaultVariant = GLOBAL_DISTINCT_VARIANTS[seatIndex % GLOBAL_DISTINCT_VARIANTS.length];
  return {
    seatId,
    seatIndex,
    accountId: null,
    publicId: null,
    displayName: `Seat ${seatIndex + 1}`,
    avatarUrl: null,
    profileColor: defaultVariant.accentHex,
    matBgCss: defaultVariant.matBgCss,
    matBorderCss: defaultVariant.matBorderCss,
    verifiedProvider: null,
    teamId: teamMode ? `team_${Math.floor(seatIndex / 2) + 1}` : null,
    status: 'empty',
    connected: false,
    awaySince: null,
    streamMode: false,
    hideClickedCards: false,
    sleeveUrl: null,
    sleeveCardName: null,
    playmatUrl: null,
    playmatCardName: null,
    playmatFocalPoint: 'center',
    life: startingLife,
    startingLife,
    playerCounters: {},
    manaPool: emptyManaPool(),
    privateNotes: '',
    savedForLater: [],
    commanderDamage: {},
    commanderTax: {},
    mulliganCount: 0,
    handConfirmed: false,
    playWithTopRevealed: false,
    revealHandsPostGame: false,
    stopPresets: [],
    flagMuted: false,
    lastFlagClearedAt: null,
    privateLook: null,
    grantedLibraryAccess: null,
    deckMeta: null,
    availableTokens: [],
    paperMode: defaultPaperSeatConfig(),
    eliminatedInfo: null,
  };
}

export function createInitialGameState(params: {
  tableId: string;
  inviteCode: string;
  hostAccountId: string;
  format?: FormatId;
  settingsOverrides?: Partial<TableSettings>;
  now?: number;
}): GameState {
  const format = params.format ?? 'commander';
  const settings = createTableSettingsFromPreset(format, params.settingsOverrides);
  const seats: Record<string, PlayerSeat> = {};
  const seatOrder: string[] = [];
  const zoneOrder: Record<string, Record<ZoneId, string[]>> = {};

  for (let i = 0; i < settings.maxSeats; i++) {
    const seat = createEmptySeat(i, settings.startingLife, settings.teamMode);
    seats[seat.seatId] = seat;
    seatOrder.push(seat.seatId);
    zoneOrder[seat.seatId] = emptyZonesForSeat();
  }

  const ts = params.now ?? Date.now();
  return {
    tableId: params.tableId,
    inviteCode: params.inviteCode,
    hostAccountId: params.hostAccountId,
    judgeAccountIds: [],
    locked: false,
    status: 'lobby',
    settings,
    lateJoinRequests: [],
    seats,
    seatOrder,
    turnDirection: 1,
    activeSeatId: seatOrder[0],
    phase: 'main1',
    turnNumber: 1,
    extraTurnsQueue: [],
    skipTurnSeatIds: [],
    awayVotes: {},
    pregame: {
      ruleZero: {
        targetBracket: null,
        fastMana: false,
        infiniteCombos: false,
        massLandDenial: false,
        stax: false,
        proxiesAllowed: true,
        houseRulesNote: '',
        agreedSeatIds: [],
      },
      highRollResults: {},
      seatingMode: 'manual',
      commandersRevealed: false,
    },
    lastTurnSnapshot: null,
    cards: {},
    zoneOrder,
    sharedZones: {
      stack: [],
      revealed: [],
    },
    flags: [],
    designations: {
      monarchSeatId: null,
      initiativeSeatId: null,
      dayNight: null,
      citysBlessingSeatIds: [],
      ringBearerBySeat: {},
      ringTemptCountBySeat: {},
      undercityRoomBySeat: {},
    },
    sharedNotes: '',
    chat: [],
    winnerSeatIds: [],
    seq: 0,
    createdAt: ts,
    updatedAt: ts,
  };
}

export function cloneGameState(state: GameState): GameState {
  return structuredClone(state);
}

function shuffleArrayWithRng<T>(items: readonly T[], rng: RngProvider): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

function getActiveSeatsInOrder(state: GameState): string[] {
  return state.seatOrder.filter((seatId) => {
    const s = state.seats[seatId];
    return s && s.accountId !== null && s.status !== 'empty' && s.status !== 'eliminated';
  });
}

function isSeatOnActiveTurn(state: GameState, seatId: string | null): boolean {
  if (!seatId) return false;
  if (seatId === state.activeSeatId) return true;
  if (state.settings.teamMode && state.settings.simultaneousTeamTurns) {
    const activeSeat = state.seats[state.activeSeatId];
    const candidate = state.seats[seatId];
    if (
      activeSeat &&
      candidate &&
      activeSeat.teamId &&
      activeSeat.teamId === candidate.teamId &&
      candidate.status !== 'eliminated'
    ) {
      return true;
    }
  }
  return false;
}

function detachCardFromCurrentZone(state: GameState, card: CardInstance): void {
  if (card.zone === 'stack' || card.zone === 'revealed') {
    const list = state.sharedZones[card.zone];
    const idx = list.indexOf(card.instanceId);
    if (idx !== -1) list.splice(idx, 1);
    return;
  }
  for (const seatZones of Object.values(state.zoneOrder)) {
    const list = seatZones[card.zone];
    const idx = list.indexOf(card.instanceId);
    if (idx !== -1) list.splice(idx, 1);
  }
}

function insertCardIntoZone(
  state: GameState,
  card: CardInstance,
  toZone: ZoneId,
  toSeatId: string,
  position: 'top' | 'bottom' | number = 'top'
): void {
  if (toZone === 'stack' || toZone === 'revealed') {
    const list = state.sharedZones[toZone];
    if (position === 'bottom') list.push(card.instanceId);
    else if (typeof position === 'number') list.splice(Math.max(0, Math.min(list.length, position)), 0, card.instanceId);
    else list.unshift(card.instanceId);
    return;
  }
  if (!state.zoneOrder[toSeatId]) {
    state.zoneOrder[toSeatId] = emptyZonesForSeat();
  }
  const list = state.zoneOrder[toSeatId][toZone];
  if (position === 'bottom') {
    list.push(card.instanceId);
  } else if (typeof position === 'number') {
    list.splice(Math.max(0, Math.min(list.length, position)), 0, card.instanceId);
  } else {
    // 'top' means index 0 for library/graveyard/exile, or end for hand/battlefield visual stability
    if (toZone === 'hand' || toZone === 'battlefield' || toZone === 'command') {
      list.push(card.instanceId);
    } else {
      list.unshift(card.instanceId);
    }
  }
}

/**
 * Pure, deterministic event reducer.
 * Never calls Date.now() or Math.random() — every state change derives solely from `event`.
 */
export function applyEvent(prevState: GameState, event: GameEvent): GameState {
  const state = cloneGameState(prevState);
  state.seq = event.seq;
  state.updatedAt = event.timestamp;

  const p = event.payload as Record<string, any>;
  const internal = (event.internalDelta ?? {}) as Record<string, any>;

  switch (event.type) {
    case 'SEAT_JOINED': {
      const seat = state.seats[p.seatId];
      if (seat) {
        seat.accountId = p.accountId;
        seat.publicId = p.publicId;
        seat.displayName = p.displayName;
        seat.avatarUrl = p.avatarUrl ?? null;
        seat.profileColor = p.profileColor ?? seat.profileColor;
        seat.verifiedProvider = (p.verifiedProvider as AuthProviderId | null) ?? null;
        if (seat.status === 'empty') {
          seat.status = state.status === 'active' ? 'playing' : 'occupied';
        }
        seat.connected = true;
        seat.awaySince = null;
        if (state.settings.forceStreamMode) {
          seat.streamMode = true;
        }
        const distinctPalettes = resolveDistinctTablePalettes(state.seats, state.seatOrder);
        for (const sid of state.seatOrder) {
          const pal = distinctPalettes[sid];
          if (pal && state.seats[sid]) {
            state.seats[sid].profileColor = pal.accentHex;
            state.seats[sid].matBgCss = pal.matBgCss;
            state.seats[sid].matBorderCss = pal.matBorderCss;
          }
        }
      }
      break;
    }

    case 'SEAT_CONNECTION_CHANGED': {
      const seat = state.seats[p.seatId];
      if (seat) {
        seat.connected = Boolean(p.connected);
        seat.awaySince = p.connected ? null : event.timestamp;
      }
      break;
    }

    case 'SEAT_LEFT': {
      const seat = state.seats[p.seatId];
      if (seat) {
        const fresh = createEmptySeat(seat.seatIndex, state.settings.startingLife, state.settings.teamMode);
        state.seats[p.seatId] = fresh;
        for (const [cid, c] of Object.entries(state.cards)) {
          if (c.ownerSeatId === p.seatId) {
            detachCardFromCurrentZone(state, c);
            delete state.cards[cid];
          }
        }
        state.zoneOrder[p.seatId] = emptyZonesForSeat();
      }
      break;
    }

    case 'SEAT_STATUS_CHANGED': {
      const seat = state.seats[p.seatId];
      if (seat && seat.status !== 'eliminated') {
        seat.status = p.status;
      }
      break;
    }

    case 'HOST_TRANSFERRED': {
      state.hostAccountId = p.newHostAccountId;
      break;
    }

    case 'JUDGE_UPDATED': {
      state.judgeAccountIds = [...(p.judgeAccountIds as string[])];
      break;
    }

    case 'TABLE_LOCK_CHANGED': {
      state.locked = Boolean(p.locked);
      break;
    }

    case 'TABLE_SETTINGS_UPDATED': {
      const prevMax = state.settings.maxSeats;
      state.settings = { ...state.settings, ...(p.patch as Partial<TableSettings>) };
      const nextMax = Math.max(1, Math.min(8, state.settings.maxSeats));
      state.settings.maxSeats = nextMax;

      if (state.status === 'lobby') {
        if (nextMax > prevMax) {
          for (let i = prevMax; i < nextMax; i++) {
            const seat = createEmptySeat(i, state.settings.startingLife, state.settings.teamMode);
            state.seats[seat.seatId] = seat;
            state.seatOrder.push(seat.seatId);
            state.zoneOrder[seat.seatId] = emptyZonesForSeat();
          }
        } else if (nextMax < prevMax) {
          for (let i = prevMax - 1; i >= nextMax; i--) {
            const sid = `seat_${i + 1}`;
            if (state.seats[sid]?.accountId === null) {
              delete state.seats[sid];
              delete state.zoneOrder[sid];
              state.seatOrder = state.seatOrder.filter((id) => id !== sid);
            }
          }
        }
        for (const seat of Object.values(state.seats)) {
          seat.startingLife = state.settings.startingLife;
          seat.life = state.settings.startingLife;
          seat.teamId = state.settings.teamMode ? `team_${Math.floor(seat.seatIndex / 2) + 1}` : null;
          if (state.settings.forceStreamMode) seat.streamMode = true;
        }
      }
      break;
    }

    case 'STREAM_MODE_TOGGLED': {
      const seat = state.seats[p.seatId];
      if (seat) {
        seat.streamMode = state.settings.forceStreamMode ? true : Boolean(p.enabled);
      }
      break;
    }

    case 'HIDE_CLICKED_TOGGLED': {
      const seat = state.seats[p.seatId];
      if (seat) {
        seat.hideClickedCards = Boolean(p.enabled);
      }
      break;
    }

    case 'DECK_LOADED': {
      const seatId = p.seatId as string;
      const seat = state.seats[seatId];
      if (!seat) break;

      // Remove existing owned cards for this seat
      for (const [cid, c] of Object.entries(state.cards)) {
        if (c.ownerSeatId === seatId) {
          detachCardFromCurrentZone(state, c);
          delete state.cards[cid];
        }
      }
      state.zoneOrder[seatId] = emptyZonesForSeat();

      const instances = internal.instances as CardInstance[];
      for (const inst of instances) {
        state.cards[inst.instanceId] = structuredClone(inst);
        state.zoneOrder[seatId][inst.zone].push(inst.instanceId);
      }
      seat.deckMeta = structuredClone(p.deckMeta);
      seat.availableTokens = structuredClone(p.availableTokens ?? []);
      if (p.requestedCosmetics) {
        if (p.requestedCosmetics.sleeveCardName !== undefined) {
          seat.sleeveCardName = p.requestedCosmetics.sleeveCardName ?? null;
        }
        if (p.requestedCosmetics.playmatCardName !== undefined) {
          seat.playmatCardName = p.requestedCosmetics.playmatCardName ?? null;
        }
        if (p.requestedCosmetics.focalPoint !== undefined) {
          seat.playmatFocalPoint = normalizeFocalPoint(p.requestedCosmetics.focalPoint);
        }
      }
      // Re-validate any previously or newly chosen card-art cosmetics against the loaded deck's legal identity pool!
      const legalPool = seat.deckMeta?.legalCosmeticArt ?? [];
      seat.sleeveUrl = resolveValidatedCardArtUrl(legalPool, seat.sleeveCardName);
      if (!seat.sleeveUrl) seat.sleeveCardName = null;
      seat.playmatUrl = resolveValidatedCardArtUrl(legalPool, seat.playmatCardName);
      if (!seat.playmatUrl) {
        seat.playmatCardName = null;
        if (!p.requestedCosmetics?.focalPoint) {
          seat.playmatFocalPoint = 'center';
        }
      }
      seat.mulliganCount = 0;
      seat.handConfirmed = false;
      seat.privateLook = null;

      // Recompute distinct identity base colors & mat tints across all seated players
      const distinctPalettes = resolveDistinctTablePalettes(state.seats, state.seatOrder);
      for (const sid of state.seatOrder) {
        const pal = distinctPalettes[sid];
        if (pal && state.seats[sid]) {
          state.seats[sid].profileColor = pal.accentHex;
          state.seats[sid].matBgCss = pal.matBgCss;
          state.seats[sid].matBorderCss = pal.matBorderCss;
        }
      }
      break;
    }

    case 'SEAT_COSMETICS_UPDATED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.sleeveCardName = (p.sleeveCardName as string | null) ?? null;
        seat.sleeveUrl = (p.sleeveUrl as string | null) ?? null;
        seat.playmatCardName = (p.playmatCardName as string | null) ?? null;
        seat.playmatUrl = (p.playmatUrl as string | null) ?? null;
        seat.playmatFocalPoint = normalizeFocalPoint(p.playmatFocalPoint ?? seat.playmatFocalPoint);
      }
      break;
    }

    case 'SEAT_COSMETICS_RESET': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.sleeveCardName = null;
        seat.sleeveUrl = null;
        seat.playmatCardName = null;
        seat.playmatUrl = null;
        seat.playmatFocalPoint = 'center';
      }
      break;
    }

    case 'MATCH_STARTED':
    case 'MATCH_RESET':
    case 'POD_REMATCHED': {
      state.status = event.type === 'MATCH_RESET' ? 'lobby' : 'active';
      state.turnNumber = 1;
      state.turnDirection = 1;
      state.phase = 'main1';
      state.extraTurnsQueue = [];
      state.skipTurnSeatIds = [];
      state.awayVotes = {};
      state.lastTurnSnapshot = null;
      state.flags = [];
      state.winnerSeatIds = [];
      state.sharedZones = { stack: [], revealed: [] };
      state.designations = {
        monarchSeatId: null,
        initiativeSeatId: null,
        dayNight: null,
        citysBlessingSeatIds: [],
        ringBearerBySeat: {},
        ringTemptCountBySeat: {},
        undercityRoomBySeat: {},
      };

      if (Array.isArray(p.newSeatOrder) && p.newSeatOrder.length === state.seatOrder.length) {
        state.seatOrder = [...(p.newSeatOrder as string[])];
        state.seatOrder.forEach((sid, idx) => {
          if (state.seats[sid]) state.seats[sid].seatIndex = idx;
        });
      }

      const startingSeatId = (p.startingSeatId as string) ?? state.seatOrder[0];
      state.activeSeatId = startingSeatId;

      const perSeatLibraryOrder = (internal.perSeatLibraryOrder ?? {}) as Record<string, string[]>;
      const perSeatOpeningHands = (internal.perSeatOpeningHands ?? {}) as Record<string, string[]>;

      // Remove all tokens and temporary single-game paper additions, and reset real cards back to initial zones
      for (const [cid, card] of Object.entries(state.cards)) {
        if (card.isToken || card.isTemporaryPaperAdd) {
          delete state.cards[cid];
          continue;
        }
        card.controllerSeatId = card.ownerSeatId;
        card.tapped = false;
        card.faceDown = false;
        card.flipped = false;
        card.counters = {};
        card.attachedToId = null;
        card.exiledByInstanceId = null;
        card.freePos = null;
        card.peekedBySeatIds = [];
        card.enteredZoneSeq = event.seq;
      }

      for (const sid of state.seatOrder) {
        const seat = state.seats[sid];
        if (!seat) continue;
        seat.life = state.settings.startingLife;
        seat.startingLife = state.settings.startingLife;
        seat.playerCounters = {};
        seat.manaPool = emptyManaPool();
        seat.commanderDamage = {};
        seat.commanderTax = {};
        seat.mulliganCount = 0;
        seat.handConfirmed = false;
        seat.playWithTopRevealed = false;
        seat.revealHandsPostGame = false;
        seat.privateLook = null;
        seat.grantedLibraryAccess = null;
        seat.eliminatedInfo = null;
        seat.paperMode.videoMarkers = [];
        seat.paperMode.scannedCardsSeen = [];
        if (seat.accountId) {
          seat.status = event.type === 'MATCH_RESET' ? 'occupied' : 'playing';
        } else {
          seat.status = 'empty';
        }

        const zones = emptyZonesForSeat();
        const ownedCards = Object.values(state.cards).filter((c) => c.ownerSeatId === sid);
        const commanders = ownedCards.filter((c) => c.isCommander && state.settings.useCommandZone);
        const sidecards = ownedCards.filter((c) => c.zone === 'sideboard');
        const sideIds = new Set(sidecards.map((c) => c.instanceId));
        const cmdIds = new Set(commanders.map((c) => c.instanceId));

        for (const cmd of commanders) {
          cmd.zone = 'command';
          zones.command.push(cmd.instanceId);
        }
        for (const sb of sidecards) {
          sb.zone = 'sideboard';
          zones.sideboard.push(sb.instanceId);
        }

        const libOrder =
          perSeatLibraryOrder[sid] ??
          ownedCards
            .filter((c) => !sideIds.has(c.instanceId) && !cmdIds.has(c.instanceId))
            .map((c) => c.instanceId);

        const openingIds = new Set(perSeatOpeningHands[sid] ?? []);
        for (const cid of perSeatOpeningHands[sid] ?? []) {
          const c = state.cards[cid];
          if (c) {
            c.zone = 'hand';
            zones.hand.push(cid);
          }
        }
        for (const cid of libOrder) {
          if (openingIds.has(cid)) continue;
          const c = state.cards[cid];
          if (c) {
            c.zone = 'library';
            zones.library.push(cid);
          }
        }
        state.zoneOrder[sid] = zones;
      }

      if (event.type === 'MATCH_RESET') {
        state.chat = [];
      }
      break;
    }

    case 'MATCH_ABANDONED': {
      state.status = 'abandoned';
      for (const seat of Object.values(state.seats)) {
        seat.life = seat.startingLife;
        seat.privateLook = null;
      }
      break;
    }

    case 'MULLIGAN_TAKEN': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      if (!seat) break;
      seat.mulliganCount = p.mulliganCount as number;
      seat.handConfirmed = false;
      seat.privateLook = null;
      const zones = state.zoneOrder[sid];
      const newLibrary = internal.libraryOrder as string[];
      const newHand = internal.handIds as string[];
      zones.library = [...newLibrary];
      zones.hand = [...newHand];
      for (const cid of newLibrary) {
        if (state.cards[cid]) state.cards[cid].zone = 'library';
      }
      for (const cid of newHand) {
        if (state.cards[cid]) state.cards[cid].zone = 'hand';
      }
      break;
    }

    case 'OPENING_HAND_CONFIRMED': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      if (!seat) break;
      seat.handConfirmed = true;
      const bottomIds = (internal.bottomCardIds as string[]) ?? [];
      if (bottomIds.length > 0) {
        const zones = state.zoneOrder[sid];
        for (const bid of bottomIds) {
          const idx = zones.hand.indexOf(bid);
          if (idx !== -1) {
            zones.hand.splice(idx, 1);
            zones.library.push(bid);
            if (state.cards[bid]) state.cards[bid].zone = 'library';
          }
        }
      }
      break;
    }

    case 'CARD_MOVED': {
      const card = state.cards[p.instanceId as string];
      if (!card) break;
      const toZone = p.toZone as ZoneId;
      const toSeatId = (p.toSeatId as string) ?? card.ownerSeatId;

      detachCardFromCurrentZone(state, card);

      // Unlink attachments & Ring-bearer if permanent leaves battlefield
      if (card.zone === 'battlefield' && toZone !== 'battlefield') {
        card.attachedToId = null;
        for (const other of Object.values(state.cards)) {
          if (other.attachedToId === card.instanceId) {
            other.attachedToId = null;
          }
        }
        for (const [rbSid, rbId] of Object.entries(state.designations.ringBearerBySeat)) {
          if (rbId === card.instanceId) {
            state.designations.ringBearerBySeat[rbSid] = null;
          }
        }
      }

      // Tokens vanish when leaving the battlefield!
      if (card.isToken && toZone !== 'battlefield') {
        delete state.cards[card.instanceId];
        break;
      }

      // Casting commander from command zone tracks commander tax automatically
      if (card.isCommander && card.zone === 'command' && (toZone === 'battlefield' || toZone === 'stack')) {
        const ownerSeat = state.seats[card.ownerSeatId];
        if (ownerSeat) {
          ownerSeat.commanderTax[card.instanceId] = (ownerSeat.commanderTax[card.instanceId] ?? 0) + 1;
        }
      }

      card.zone = toZone;
      card.controllerSeatId = toZone === 'battlefield' ? toSeatId : card.ownerSeatId;
      card.tapped = toZone === 'battlefield' ? Boolean(p.tapped) : false;
      card.faceDown = Boolean(p.faceDown);
      if (toZone !== 'battlefield') {
        card.flipped = false;
        card.counters = toZone === 'exile' ? card.counters : {};
        card.freePos = null;
      } else {
        card.row = (p.row as BattlefieldRow) ?? classifyBattlefieldRow(card.card.typeLine);
        card.freePos = p.freePos ?? null;
      }
      card.exiledByInstanceId = toZone === 'exile' ? (p.exiledByInstanceId ?? null) : null;
      card.peekedBySeatIds = card.faceDown ? [card.controllerSeatId] : [];
      card.enteredZoneSeq = event.seq;

      insertCardIntoZone(
        state,
        card,
        toZone,
        toZone === 'battlefield' ? card.controllerSeatId : card.ownerSeatId,
        p.position ?? 'top'
      );

      // Remove from active privateLook if picked out of library
      for (const s of Object.values(state.seats)) {
        if (s.privateLook) {
          s.privateLook.cardInstanceIds = s.privateLook.cardInstanceIds.filter((id) => id !== card.instanceId);
          if (s.privateLook.cardInstanceIds.length === 0) s.privateLook = null;
        }
      }
      break;
    }

    case 'CARD_TAPPED': {
      const card = state.cards[p.instanceId as string];
      if (card) {
        card.tapped = Boolean(p.tapped);
      }
      break;
    }

    case 'ALL_UNTAPPED': {
      const sid = p.seatId as string;
      for (const cid of state.zoneOrder[sid]?.battlefield ?? []) {
        if (state.cards[cid]) state.cards[cid].tapped = false;
      }
      break;
    }

    case 'CARD_FLIPPED': {
      const card = state.cards[p.instanceId as string];
      if (card) card.flipped = !card.flipped;
      break;
    }

    case 'CARD_FACE_DOWN_TOGGLED': {
      const card = state.cards[p.instanceId as string];
      if (card) {
        card.faceDown = !card.faceDown;
        card.peekedBySeatIds = card.faceDown ? [card.controllerSeatId] : [];
      }
      break;
    }

    case 'CARD_PEEKED': {
      const card = state.cards[p.instanceId as string];
      const viewerSeatId = p.seatId as string;
      if (card && !card.peekedBySeatIds.includes(viewerSeatId)) {
        card.peekedBySeatIds.push(viewerSeatId);
      }
      break;
    }

    case 'CARD_ATTACHED': {
      const card = state.cards[p.instanceId as string];
      if (card) {
        card.attachedToId = (p.targetInstanceId as string | null) ?? null;
      }
      break;
    }

    case 'CARD_CONTROLLER_CHANGED': {
      const card = state.cards[p.instanceId as string];
      const newController = p.newControllerSeatId as string;
      if (card && card.zone === 'battlefield' && state.seats[newController]) {
        detachCardFromCurrentZone(state, card);
        card.controllerSeatId = newController;
        insertCardIntoZone(state, card, 'battlefield', newController, 'top');
      }
      break;
    }

    case 'CARD_COUNTER_ADJUSTED': {
      const card = state.cards[p.instanceId as string];
      if (card) {
        const counter = p.counter as string;
        const next = (card.counters[counter] ?? 0) + (p.delta as number);
        if (next <= 0) delete card.counters[counter];
        else card.counters[counter] = next;
      }
      break;
    }

    case 'TOKENS_CREATED': {
      const instances = internal.instances as CardInstance[];
      for (const inst of instances) {
        state.cards[inst.instanceId] = structuredClone(inst);
        insertCardIntoZone(state, inst, 'battlefield', inst.controllerSeatId, 'top');
      }
      break;
    }

    case 'CARDS_DRAWN': {
      const sid = p.seatId as string;
      if (state.seats[sid]) state.seats[sid].grantedLibraryAccess = null;
      const drawnIds = internal.drawnIds as string[];
      for (const cid of drawnIds) {
        const card = state.cards[cid];
        if (!card) continue;
        detachCardFromCurrentZone(state, card);
        card.zone = 'hand';
        card.faceDown = false;
        card.enteredZoneSeq = event.seq;
        state.zoneOrder[sid].hand.push(cid);
      }
      break;
    }

    case 'CARDS_MILLED': {
      const sid = p.seatId as string;
      if (state.seats[sid]) state.seats[sid].grantedLibraryAccess = null;
      const milledIds = p.milledIds as string[];
      for (const cid of milledIds) {
        const card = state.cards[cid];
        if (!card) continue;
        detachCardFromCurrentZone(state, card);
        card.zone = 'graveyard';
        card.faceDown = false;
        card.enteredZoneSeq = event.seq;
        state.zoneOrder[sid].graveyard.unshift(cid);
      }
      break;
    }

    case 'PRIVATE_LOOK_STARTED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.privateLook = {
          kind: p.kind,
          count: p.count,
          cardInstanceIds: [...(internal.cardInstanceIds as string[])],
        };
      }
      break;
    }

    case 'SCRY_RESOLVED': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      if (seat) {
        seat.privateLook = null;
        seat.grantedLibraryAccess = null;
      }
      const topIds = internal.topIds as string[];
      const bottomIds = internal.bottomIds as string[];
      const moving = new Set([...topIds, ...bottomIds]);
      const rest = state.zoneOrder[sid].library.filter((id) => !moving.has(id));
      state.zoneOrder[sid].library = [...topIds, ...rest, ...bottomIds];
      break;
    }

    case 'PRIVATE_LOOK_CLOSED': {
      const seat = state.seats[p.seatId as string];
      if (seat) seat.privateLook = null;
      break;
    }

    case 'TOP_CARD_REVEALED': {
      const card = state.cards[p.instanceId as string];
      if (card) {
        if (state.seats[card.ownerSeatId]) state.seats[card.ownerSeatId].grantedLibraryAccess = null;
        detachCardFromCurrentZone(state, card);
        card.zone = 'revealed';
        card.faceDown = false;
        state.sharedZones.revealed.unshift(card.instanceId);
      }
      break;
    }

    case 'LIBRARY_SHUFFLED': {
      const sid = p.seatId as string;
      const newOrder = internal.newOrder as string[];
      if (state.zoneOrder[sid] && Array.isArray(newOrder)) {
        state.zoneOrder[sid].library = [...newOrder];
      }
      if (state.seats[sid]) {
        state.seats[sid].privateLook = null;
        state.seats[sid].grantedLibraryAccess = null;
      }
      break;
    }

    case 'PLAY_TOP_REVEALED_TOGGLED': {
      const seat = state.seats[p.seatId as string];
      if (seat) seat.playWithTopRevealed = Boolean(p.enabled);
      break;
    }

    case 'LIFE_ADJUSTED': {
      const targetSeat = state.seats[p.targetSeatId as string];
      if (targetSeat) {
        targetSeat.life += p.delta as number;
        if (state.settings.teamMode && state.settings.sharedTeamLife && targetSeat.teamId) {
          for (const other of Object.values(state.seats)) {
            if (other.seatId !== targetSeat.seatId && other.teamId === targetSeat.teamId) {
              other.life = targetSeat.life;
            }
          }
        }
      }
      break;
    }

    case 'PLAYER_COUNTER_ADJUSTED': {
      const seat = state.seats[p.targetSeatId as string];
      if (seat) {
        const key = p.counter as string;
        const next = (seat.playerCounters[key] ?? 0) + (p.delta as number);
        if (next <= 0) delete seat.playerCounters[key];
        else seat.playerCounters[key] = next;

        // Sync shared poison counters in Two-Headed Giant / Team shared life mode
        if (key === 'poison' && state.settings.teamMode && state.settings.sharedTeamLife && seat.teamId) {
          for (const other of Object.values(state.seats)) {
            if (other.seatId !== seat.seatId && other.teamId === seat.teamId) {
              if (next <= 0) delete other.playerCounters[key];
              else other.playerCounters[key] = next;
            }
          }
        }
      }
      break;
    }

    case 'COMMANDER_DAMAGE_ADJUSTED': {
      const seat = state.seats[p.targetSeatId as string];
      if (seat) {
        const key = `${p.sourceSeatId}:${p.commanderInstanceId}`;
        const next = Math.max(0, (seat.commanderDamage[key] ?? 0) + (p.delta as number));
        if (next === 0) delete seat.commanderDamage[key];
        else seat.commanderDamage[key] = next;
      }
      break;
    }

    case 'COMMANDER_TAX_ADJUSTED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        const cid = p.commanderInstanceId as string;
        const next = Math.max(0, (seat.commanderTax[cid] ?? 0) + (p.delta as number));
        if (next === 0) delete seat.commanderTax[cid];
        else seat.commanderTax[cid] = next;
      }
      break;
    }

    case 'DESIGNATION_SET': {
      const d = p.designation as string;
      const sid = (p.seatId as string | null) ?? null;
      if (d === 'monarch') state.designations.monarchSeatId = sid;
      else if (d === 'initiative') state.designations.initiativeSeatId = sid;
      else if (d === 'day') state.designations.dayNight = 'day';
      else if (d === 'night') state.designations.dayNight = 'night';
      else if (d === 'citys_blessing' && sid) {
        if (state.designations.citysBlessingSeatIds.includes(sid)) {
          state.designations.citysBlessingSeatIds = state.designations.citysBlessingSeatIds.filter((x) => x !== sid);
        } else {
          state.designations.citysBlessingSeatIds.push(sid);
        }
      }
      break;
    }

    case 'PHASE_CHANGED': {
      state.phase = p.phase as GamePhase;
      break;
    }

    case 'TURN_PASSED': {
      state.lastTurnSnapshot = {
        previousActiveSeatId: state.activeSeatId,
        previousTurnNumber: state.turnNumber,
        previousPhase: state.phase,
      };
      state.activeSeatId = p.nextSeatId as string;
      state.turnNumber = p.nextTurnNumber as number;
      if (Array.isArray(p.nextExtraTurnsQueue)) {
        state.extraTurnsQueue = [...(p.nextExtraTurnsQueue as string[])];
      }
      if (Array.isArray(p.nextSkipTurnSeatIds)) {
        state.skipTurnSeatIds = [...(p.nextSkipTurnSeatIds as string[])];
      }
      state.awayVotes = {};
      state.phase = 'untap';
      break;
    }

    case 'PASS_TURN_UNDONE': {
      if (state.lastTurnSnapshot) {
        state.activeSeatId = state.lastTurnSnapshot.previousActiveSeatId;
        state.turnNumber = state.lastTurnSnapshot.previousTurnNumber;
        state.phase = state.lastTurnSnapshot.previousPhase;
        state.lastTurnSnapshot = null;
      }
      break;
    }

    case 'TURN_DIRECTION_REVERSED': {
      state.turnDirection = state.turnDirection === 1 ? -1 : 1;
      break;
    }

    case 'FLAG_RAISED': {
      const existing = state.flags.find((f) => f.seatId === p.seatId);
      if (!existing) {
        const nextOrder = state.flags.reduce((m, f) => Math.max(m, f.order), 0) + 1;
        state.flags.push({
          flagId: p.flagId,
          seatId: p.seatId,
          reason: p.reason,
          order: nextOrder,
          status: 'open',
          createdAt: event.timestamp,
        });
      }
      break;
    }

    case 'FLAG_SEEN': {
      const f = state.flags.find((fl) => fl.flagId === p.flagId);
      if (f) f.status = 'seen';
      break;
    }

    case 'FLAG_CLEARED': {
      const cleared = state.flags.find((fl) => fl.flagId === p.flagId);
      if (cleared && state.seats[cleared.seatId]) {
        state.seats[cleared.seatId].lastFlagClearedAt = event.timestamp;
      }
      state.flags = state.flags.filter((fl) => fl.flagId !== p.flagId);
      break;
    }

    case 'PLAYER_ELIMINATED': {
      const primarySid = p.targetSeatId as string;
      const primarySeat = state.seats[primarySid];
      if (primarySeat) {
        const targetsToEliminate = [primarySid];
        if (state.settings.teamMode && state.settings.sharedTeamLife && primarySeat.teamId) {
          for (const other of Object.values(state.seats)) {
            if (other.seatId !== primarySid && other.teamId === primarySeat.teamId && other.status !== 'eliminated') {
              targetsToEliminate.push(other.seatId);
            }
          }
        }

        for (const sid of targetsToEliminate) {
          const seat = state.seats[sid];
          if (!seat) continue;
          seat.status = 'eliminated';
          seat.privateLook = null;
          seat.grantedLibraryAccess = null;
          seat.eliminatedInfo = {
            turn: state.turnNumber,
            reason: p.reason,
            eliminatedBySeatId: p.eliminatedBySeatId ?? null,
          };
          state.flags = state.flags.filter((f) => f.seatId !== sid);

          if (state.settings.removeCardsOnElimination) {
            for (const [cid, c] of Object.entries(state.cards)) {
              if (c.ownerSeatId === sid || c.controllerSeatId === sid) {
                if (c.ownerSeatId === sid) {
                  detachCardFromCurrentZone(state, c);
                  delete state.cards[cid];
                } else {
                  detachCardFromCurrentZone(state, c);
                  c.controllerSeatId = c.ownerSeatId;
                  insertCardIntoZone(state, c, 'battlefield', c.ownerSeatId, 'top');
                }
              }
            }
            state.zoneOrder[sid] = emptyZonesForSeat();
          }
        }

        const remaining = getActiveSeatsInOrder(state);
        if (targetsToEliminate.includes(state.activeSeatId) && remaining.length > 0) {
          state.activeSeatId = remaining[0];
          state.phase = 'main1';
        }
        const totalOccupied = Object.values(state.seats).filter((s) => s.accountId !== null).length;
        if (state.settings.teamMode) {
          const survivingTeams = new Set(
            remaining.map((sid) => state.seats[sid]?.teamId ?? sid)
          );
          if (survivingTeams.size === 1 && totalOccupied >= 2) {
            state.status = 'finished';
            state.winnerSeatIds = [...remaining];
          }
        } else if (remaining.length === 1 && totalOccupied >= 2) {
          state.status = 'finished';
          state.winnerSeatIds = [remaining[0]];
        }
      }
      break;
    }

    case 'SEATS_REORDERED': {
      const nextOrder = p.newSeatOrder as string[];
      if (Array.isArray(nextOrder) && nextOrder.length === state.seatOrder.length) {
        state.seatOrder = [...nextOrder];
        state.seatOrder.forEach((sid, idx) => {
          if (state.seats[sid]) state.seats[sid].seatIndex = idx;
        });
      }
      break;
    }

    case 'TEAM_ASSIGNED': {
      const seat = state.seats[p.targetSeatId as string];
      if (seat) {
        seat.teamId = (p.teamId as string | null) ?? null;
        if (state.settings.teamMode && state.settings.sharedTeamLife && seat.teamId) {
          const mate = Object.values(state.seats).find(
            (s) => s.seatId !== seat.seatId && s.teamId === seat.teamId
          );
          if (mate) {
            seat.life = mate.life;
            if (mate.playerCounters.poison) {
              seat.playerCounters.poison = mate.playerCounters.poison;
            }
          }
        }
      }
      break;
    }

    case 'EXTRA_TURN_ADDED': {
      state.extraTurnsQueue.push(p.targetSeatId as string);
      break;
    }

    case 'SKIP_TURN_ADDED': {
      const targetSid = p.targetSeatId as string;
      if (!state.skipTurnSeatIds.includes(targetSid)) {
        state.skipTurnSeatIds.push(targetSid);
      }
      break;
    }

    case 'AWAY_VOTE_RECORDED': {
      const targetSid = p.targetSeatId as string;
      const action = p.action as 'skip_turn' | 'remove';
      const voterSid = p.voterSeatId as string;
      const cur = state.awayVotes[targetSid];
      if (!cur || cur.action !== action) {
        state.awayVotes[targetSid] = { action, voterSeatIds: [voterSid] };
      } else if (!cur.voterSeatIds.includes(voterSid)) {
        cur.voterSeatIds.push(voterSid);
      }
      break;
    }

    case 'RULE_ZERO_UPDATED': {
      const patch = p.patch as Record<string, any>;
      state.pregame.ruleZero = {
        ...state.pregame.ruleZero,
        ...patch,
        agreedSeatIds: p.actorSeatId ? [p.actorSeatId as string] : [],
      };
      break;
    }

    case 'RULE_ZERO_AGREED': {
      const sid = p.seatId as string;
      const agreed = Boolean(p.agreed);
      const list = state.pregame.ruleZero.agreedSeatIds;
      if (agreed && !list.includes(sid)) list.push(sid);
      if (!agreed) {
        state.pregame.ruleZero.agreedSeatIds = list.filter((x) => x !== sid);
      }
      break;
    }

    case 'PREGAME_SEATING_APPLIED': {
      const nextOrder = p.newSeatOrder as string[];
      if (Array.isArray(nextOrder) && nextOrder.length === state.seatOrder.length) {
        state.seatOrder = [...nextOrder];
        state.seatOrder.forEach((sid, idx) => {
          if (state.seats[sid]) state.seats[sid].seatIndex = idx;
        });
      }
      if (p.startingSeatId) {
        state.activeSeatId = p.startingSeatId as string;
      }
      state.pregame.seatingMode = p.mode;
      state.pregame.highRollResults = { ...(p.highRollResults ?? {}) };
      break;
    }

    case 'PREGAME_COMMANDERS_REVEALED': {
      state.pregame.commandersRevealed = true;
      for (const sid of state.seatOrder) {
        const seat = state.seats[sid];
        if (seat?.deckMeta) {
          seat.deckMeta.showTransparency = true;
        }
      }
      break;
    }

    case 'STOP_PRESETS_UPDATED': {
      const seat = state.seats[p.seatId as string];
      if (seat && Array.isArray(p.phases)) {
        seat.stopPresets = [...(p.phases as GamePhase[])];
      }
      break;
    }

    case 'FLAG_MUTE_UPDATED': {
      const seat = state.seats[p.targetSeatId as string];
      if (seat) {
        seat.flagMuted = Boolean(p.muted);
        if (seat.flagMuted) {
          state.flags = state.flags.filter((f) => f.seatId !== seat.seatId);
        }
      }
      break;
    }

    case 'LIBRARY_ACCESS_GRANTED': {
      const owner = state.seats[p.ownerSeatId as string];
      if (owner) {
        owner.grantedLibraryAccess = {
          granteeSeatId: p.granteeSeatId as string,
          scope: p.scope as 'top' | 'entire',
          count: p.count as number,
          takeFaceDown: Boolean(p.takeFaceDown),
          cardInstanceIds: [...(internal.cardInstanceIds as string[])],
          grantedAt: event.timestamp,
        };
      }
      break;
    }

    case 'GRANTED_LIBRARY_CARD_TAKEN': {
      const ownerSid = p.ownerSeatId as string;
      const granteeSid = p.granteeSeatId as string;
      const cid = p.instanceId as string;
      const dest = p.destination as 'exile' | 'hand' | 'battlefield';
      const takeFaceDown = Boolean(p.takeFaceDown);
      const card = state.cards[cid];
      if (card) {
        detachCardFromCurrentZone(state, card);
        card.zone = dest;
        card.controllerSeatId = granteeSid;
        card.faceDown = takeFaceDown;
        card.peekedBySeatIds = takeFaceDown ? [granteeSid] : [];
        card.enteredZoneSeq = event.seq;
        insertCardIntoZone(state, card, dest, granteeSid, 'top');
      }
      const owner = state.seats[ownerSid];
      if (owner?.grantedLibraryAccess) {
        owner.grantedLibraryAccess.cardInstanceIds = owner.grantedLibraryAccess.cardInstanceIds.filter(
          (id) => id !== cid
        );
        if (owner.grantedLibraryAccess.cardInstanceIds.length === 0) {
          owner.grantedLibraryAccess = null;
        }
      }
      break;
    }

    case 'LIBRARY_ACCESS_REVOKED': {
      const owner = state.seats[p.ownerSeatId as string];
      if (owner) {
        owner.grantedLibraryAccess = null;
      }
      break;
    }

    case 'MANA_POOL_ADJUSTED': {
      const seat = state.seats[p.seatId as string];
      const color = p.color as ManaPoolColor;
      if (seat && color in seat.manaPool) {
        seat.manaPool[color] = Math.max(0, (seat.manaPool[color] ?? 0) + (p.delta as number));
      }
      break;
    }

    case 'MANA_POOL_CLEARED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.manaPool = emptyManaPool();
      }
      break;
    }

    case 'RING_BEARER_SET': {
      const sid = p.seatId as string;
      state.designations.ringBearerBySeat[sid] = (p.instanceId as string | null) ?? null;
      if (typeof p.temptCount === 'number') {
        state.designations.ringTemptCountBySeat[sid] = Math.max(0, Math.min(4, p.temptCount));
      }
      break;
    }

    case 'UNDERCITY_ROOM_SET': {
      const sid = p.seatId as string;
      const room = (p.room as string | null) ?? null;
      state.designations.undercityRoomBySeat[sid] = room;
      if (room !== null) {
        state.designations.initiativeSeatId = sid;
      }
      break;
    }

    case 'CARDS_REVEALED_UNTIL': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      if (seat) {
        seat.grantedLibraryAccess = null;
      }
      const flippedIds = (internal.flippedIds as string[]) ?? [];
      if (p.privateOnly) {
        if (seat) {
          seat.privateLook = {
            kind: 'look_top',
            count: flippedIds.length,
            cardInstanceIds: [...flippedIds],
          };
        }
      } else {
        for (const cid of flippedIds) {
          const card = state.cards[cid];
          if (!card) continue;
          detachCardFromCurrentZone(state, card);
          card.zone = 'revealed';
          card.faceDown = false;
          card.enteredZoneSeq = event.seq;
          state.sharedZones.revealed.push(cid);
        }
      }
      break;
    }

    case 'REVEALED_UNTIL_RESOLVED': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      if (seat) {
        seat.privateLook = null;
        seat.grantedLibraryAccess = null;
      }
      const hitId = (internal.hitInstanceId as string | null) ?? null;
      const hitDest = p.hitDestination as 'battlefield' | 'hand' | 'stack' | 'exile';
      if (hitId && state.cards[hitId]) {
        const hitCard = state.cards[hitId];
        detachCardFromCurrentZone(state, hitCard);
        hitCard.zone = hitDest;
        hitCard.controllerSeatId = sid;
        hitCard.faceDown = false;
        hitCard.tapped = false;
        if (hitDest === 'battlefield') {
          hitCard.row = classifyBattlefieldRow(hitCard.card.typeLine);
        }
        hitCard.enteredZoneSeq = event.seq;
        insertCardIntoZone(state, hitCard, hitDest, sid, 'top');
      }
      const bottomedIds = (internal.bottomedIds as string[]) ?? [];
      for (const cid of bottomedIds) {
        const card = state.cards[cid];
        if (!card) continue;
        detachCardFromCurrentZone(state, card);
        card.zone = 'library';
        card.faceDown = false;
        card.enteredZoneSeq = event.seq;
        state.zoneOrder[sid].library.push(cid);
      }
      break;
    }

    case 'RANDOM_TARGET_PICKED': {
      break;
    }

    case 'PRIVATE_NOTES_UPDATED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.privateNotes = String(internal.notes ?? '').slice(0, 4000);
      }
      break;
    }

    case 'SAVED_FOR_LATER_TOGGLED': {
      const seat = state.seats[p.seatId as string];
      const name = String(internal.cardName ?? '').trim();
      if (seat && name) {
        const idx = seat.savedForLater.findIndex((n) => n.toLowerCase() === name.toLowerCase());
        if (idx !== -1) {
          seat.savedForLater.splice(idx, 1);
        } else if (seat.savedForLater.length < 50) {
          seat.savedForLater.push(name);
        }
      }
      break;
    }

    case 'SHARED_NOTES_UPDATED': {
      state.sharedNotes = String(p.notes ?? '').slice(0, 4000);
      break;
    }

    case 'CHAT_MESSAGE_SENT': {
      state.chat.push(structuredClone(p.message));
      if (state.chat.length > 250) {
        state.chat = state.chat.slice(-250);
      }
      break;
    }

    case 'CHAT_MESSAGE_EDITED': {
      const msg = state.chat.find((m) => m.id === (p.messageId as string));
      if (msg && !msg.deleted) {
        msg.text = String(p.newText ?? '').slice(0, 500);
        msg.editedAt = event.timestamp;
      }
      break;
    }

    case 'CHAT_MESSAGE_DELETED': {
      const msg = state.chat.find((m) => m.id === (p.messageId as string));
      if (msg) {
        msg.deleted = true;
        msg.text = '[Message deleted]';
      }
      break;
    }

    case 'CHAT_REACTION_TOGGLED': {
      const msg = state.chat.find((m) => m.id === (p.messageId as string));
      const emoji = String(p.emoji ?? '').trim().slice(0, 8);
      const actorPub = String(p.actorPublicId ?? '');
      if (msg && emoji && actorPub) {
        if (!msg.reactions) msg.reactions = {};
        const cur = msg.reactions[emoji] ?? [];
        if (cur.includes(actorPub)) {
          const next = cur.filter((id) => id !== actorPub);
          if (next.length === 0) delete msg.reactions[emoji];
          else msg.reactions[emoji] = next;
        } else {
          msg.reactions[emoji] = [...cur, actorPub];
        }
      }
      break;
    }

    case 'MATCH_PAUSED_SAVED': {
      if (state.status === 'active') {
        state.status = 'paused';
      }
      break;
    }

    case 'MATCH_RESUMED': {
      if (state.status === 'paused') {
        state.status = 'active';
      }
      break;
    }

    case 'POSTGAME_HAND_REVEAL_TOGGLED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.revealHandsPostGame = Boolean(p.enabled);
      }
      break;
    }

    case 'LATE_JOIN_REQUESTED': {
      const req = structuredClone(p.request) as GameState['lateJoinRequests'][number];
      if (!state.lateJoinRequests) state.lateJoinRequests = [];
      state.lateJoinRequests = state.lateJoinRequests.filter((r) => r.publicId !== req.publicId);
      state.lateJoinRequests.push(req);
      break;
    }

    case 'LATE_JOIN_RESOLVED': {
      const requestId = p.requestId as string;
      if (state.lateJoinRequests) {
        state.lateJoinRequests = state.lateJoinRequests.filter((r) => r.requestId !== requestId);
      }
      break;
    }

    case 'PAPER_MODE_CONFIGURED': {
      const seat = state.seats[p.seatId as string];
      if (seat) {
        seat.paperMode = {
          ...seat.paperMode,
          ...(p.patch as Partial<PaperSeatConfig>),
          tableOnlyCrop: p.patch?.tableOnlyCrop
            ? { ...p.patch.tableOnlyCrop }
            : { ...seat.paperMode.tableOnlyCrop },
        };
      }
      break;
    }

    case 'PAPER_CARD_SCANNED': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      if (!seat) break;

      const toZone = (p.toZone as 'battlefield' | 'graveyard' | 'exile' | 'command' | 'revealed') ?? 'battlefield';
      const movedInstanceId = (internal.movedInstanceId as string | null) ?? null;
      const spawnedTokens = (internal.spawnedTokens as CardInstance[] | undefined) ?? [];

      if (movedInstanceId && state.cards[movedInstanceId]) {
        const card = state.cards[movedInstanceId];
        if (card.zone === 'library') {
          seat.grantedLibraryAccess = null;
        }
        detachCardFromCurrentZone(state, card);
        card.zone = toZone;
        card.controllerSeatId = sid;
        card.faceDown = false;
        card.tapped = false;
        if (toZone === 'battlefield') {
          card.row = classifyBattlefieldRow(card.card.typeLine);
        }
        card.enteredZoneSeq = event.seq;
        insertCardIntoZone(state, card, toZone, sid, 'top');
      }

      for (const tok of spawnedTokens) {
        const cloned = structuredClone(tok);
        state.cards[cloned.instanceId] = cloned;
        insertCardIntoZone(state, cloned, cloned.zone, sid, 'top');
      }

      if (p.scanEntry) {
        seat.paperMode.scannedCardsSeen.unshift(structuredClone(p.scanEntry));
        if (seat.paperMode.scannedCardsSeen.length > 120) {
          seat.paperMode.scannedCardsSeen = seat.paperMode.scannedCardsSeen.slice(0, 120);
        }
      }
      break;
    }

    case 'PAPER_CARD_ADDED_LIVE': {
      const sid = p.seatId as string;
      const seat = state.seats[sid];
      const newCard = structuredClone(internal.cardInstance as CardInstance | undefined);
      if (seat && newCard) {
        state.cards[newCard.instanceId] = newCard;
        insertCardIntoZone(state, newCard, newCard.zone, sid, 'top');
        if (p.scope === 'save_deck_update' && seat.deckMeta) {
          seat.deckMeta.mainCount += 1;
        }
        if (p.scanEntry) {
          seat.paperMode.scannedCardsSeen.unshift(structuredClone(p.scanEntry));
        }
      }
      break;
    }

    case 'VIDEO_MARKER_SET': {
      const seat = state.seats[p.targetSeatId as string];
      const marker = structuredClone(p.marker as PaperVideoMarker | undefined);
      if (seat && marker) {
        const idx = seat.paperMode.videoMarkers.findIndex((m) => m.markerId === marker.markerId);
        if (idx !== -1) {
          seat.paperMode.videoMarkers[idx] = marker;
        } else if (seat.paperMode.videoMarkers.length < 40) {
          seat.paperMode.videoMarkers.push(marker);
        }
      }
      break;
    }

    case 'VIDEO_MARKER_CLEARED': {
      const seat = state.seats[p.targetSeatId as string];
      const markerId = p.markerId as string;
      if (seat) {
        if (markerId === 'all') {
          seat.paperMode.videoMarkers = [];
        } else {
          seat.paperMode.videoMarkers = seat.paperMode.videoMarkers.filter((m) => m.markerId !== markerId);
        }
      }
      break;
    }

    case 'PAPER_ALTER_REGISTERED': {
      const seat = state.seats[p.seatId as string];
      const alter = internal.alter as PaperSeatConfig['registeredAlters'][number] | undefined;
      if (seat && alter) {
        seat.paperMode.registeredAlters = seat.paperMode.registeredAlters.filter(
          (a) => a.cardName.toLowerCase() !== alter.cardName.toLowerCase()
        );
        seat.paperMode.registeredAlters.push({ ...alter });
      }
      break;
    }

    default:
      break;
  }

  return state;
}

export function replayEvents(initialState: GameState, events: readonly GameEvent[]): GameState {
  let current = cloneGameState(initialState);
  for (const ev of events) {
    current = applyEvent(current, ev);
  }
  return current;
}

function makeEvent(
  state: GameState,
  seq: number,
  now: number,
  actor: IntentActor,
  type: string,
  summary: string,
  payload: Record<string, unknown>,
  internalDelta?: Record<string, unknown>
): GameEvent {
  return {
    seq,
    timestamp: now,
    turnNumber: state.turnNumber,
    actorSeatId: actor.seatId,
    actorName: actor.displayName,
    type,
    summary,
    payload,
    internalDelta,
  };
}

/**
 * Validates authorization + business rules, builds append-only event(s), and reduces next state.
 */
export function processIntent(
  currentState: GameState,
  initialSnapshot: GameState,
  eventLog: readonly GameEvent[],
  intent: GameIntent,
  actor: IntentActor,
  rng: RngProvider
): IntentResult {
  const now = rng.now();
  const seat = actor.seatId ? currentState.seats[actor.seatId] : null;
  const isSeatedPlayer = Boolean(seat && seat.accountId === actor.accountId && seat.status !== 'eliminated');
  const hasAdminPower = actor.isHost || actor.isJudge;

  const ownsSeatAnyState = Boolean(seat && seat.accountId === actor.accountId);

  // Spectators / unseated users can only use chat actions; eliminated players may also toggle post-game hand reveal
  if (!isSeatedPlayer && !hasAdminPower) {
    const allowedChatIntents = new Set(['SEND_CHAT', 'EDIT_CHAT', 'DELETE_CHAT', 'REACT_CHAT']);
    const allowedEliminatedIntents = ownsSeatAnyState && intent.type === 'TOGGLE_POSTGAME_HAND_REVEAL';
    if (!allowedChatIntents.has(intent.type) && !allowedEliminatedIntents) {
      return {
        ok: false,
        error: 'Only active seated players, the host, or a judge may perform game actions.',
        state: currentState,
        emittedEvents: [],
      };
    }
  }

  let nextSeq = currentState.seq + 1;
  const events: GameEvent[] = [];

  switch (intent.type) {
    case 'SET_SEAT_STATUS': {
      if (!seat) return { ok: false, error: 'You are not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'SEAT_STATUS_CHANGED', `${actor.displayName} is now ${intent.status.toUpperCase()}.`, {
          seatId: seat.seatId,
          status: intent.status,
        })
      );
      break;
    }

    case 'UPDATE_TABLE_SETTINGS': {
      if (!hasAdminPower) {
        return { ok: false, error: 'Only the host or judge can modify table settings.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'TABLE_SETTINGS_UPDATED', `${actor.displayName} updated table settings.`, {
          patch: intent.patch,
        })
      );
      break;
    }

    case 'TOGGLE_STREAM_MODE': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'STREAM_MODE_TOGGLED',
          `${actor.displayName} turned Stream Mode ${intent.enabled ? 'ON' : 'OFF'}.`,
          { seatId: seat.seatId, enabled: intent.enabled }
        )
      );
      break;
    }

    case 'TOGGLE_HIDE_CLICKED_CARDS': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'HIDE_CLICKED_TOGGLED', `${actor.displayName} updated private inspection.`, {
          seatId: seat.seatId,
          enabled: intent.enabled,
        })
      );
      break;
    }

    case 'SET_SEAT_COSMETICS': {
      if (!seat) return { ok: false, error: 'You must sit in a seat to configure sleeves or playmats.', state: currentState, emittedEvents: [] };
      const legalPool = seat.deckMeta?.legalCosmeticArt ?? [];

      const rawSleeveName = intent.sleeveCardName !== undefined ? intent.sleeveCardName : seat.sleeveCardName;
      const rawPlaymatName = intent.playmatCardName !== undefined ? intent.playmatCardName : seat.playmatCardName;

      // Reject external URLs outright
      if (
        (rawSleeveName && /^(https?:|data:|blob:)/i.test(rawSleeveName.trim())) ||
        (rawPlaymatName && /^(https?:|data:|blob:)/i.test(rawPlaymatName.trim()))
      ) {
        return {
          ok: false,
          error: 'External image uploads/URLs are disabled. Choose official card art from your deck or color identity.',
          state: currentState,
          emittedEvents: [],
        };
      }

      let nextSleeveUrl: string | null = null;
      if (rawSleeveName !== null && rawSleeveName !== undefined && rawSleeveName.trim() !== '') {
        nextSleeveUrl = resolveValidatedCardArtUrl(legalPool, rawSleeveName);
        if (!nextSleeveUrl) {
          return {
            ok: false,
            error: `"${rawSleeveName}" is not in your deck or legal color-identity card art pool.`,
            state: currentState,
            emittedEvents: [],
          };
        }
      }

      let nextPlaymatUrl: string | null = null;
      if (rawPlaymatName !== null && rawPlaymatName !== undefined && rawPlaymatName.trim() !== '') {
        nextPlaymatUrl = resolveValidatedCardArtUrl(legalPool, rawPlaymatName);
        if (!nextPlaymatUrl) {
          return {
            ok: false,
            error: `"${rawPlaymatName}" is not in your deck or legal color-identity card art pool.`,
            state: currentState,
            emittedEvents: [],
          };
        }
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'SEAT_COSMETICS_UPDATED',
          `${actor.displayName} updated their sleeves & playmat.`,
          {
            seatId: seat.seatId,
            sleeveCardName: nextSleeveUrl ? rawSleeveName!.trim() : null,
            sleeveUrl: nextSleeveUrl,
            playmatCardName: nextPlaymatUrl ? rawPlaymatName!.trim() : null,
            playmatUrl: nextPlaymatUrl,
            playmatFocalPoint: normalizeFocalPoint(intent.playmatFocalPoint ?? seat.playmatFocalPoint),
          }
        )
      );
      break;
    }

    case 'RESET_SEAT_COSMETICS': {
      const targetSeat = currentState.seats[intent.targetSeatId];
      if (!targetSeat) {
        return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      }
      if (!hasAdminPower && seat?.seatId !== targetSeat.seatId) {
        return {
          ok: false,
          error: 'Only the table host, a judge, or the seat owner can reset a seat’s cosmetics.',
          state: currentState,
          emittedEvents: [],
        };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'SEAT_COSMETICS_RESET',
          `${actor.displayName} reset ${targetSeat.displayName}'s sleeves & playmat to defaults.`,
          { seatId: targetSeat.seatId }
        )
      );
      break;
    }

    case 'LOAD_DECK': {
      if (!seat) return { ok: false, error: 'You must sit in a seat to load a deck.', state: currentState, emittedEvents: [] };
      if (currentState.status === 'active' || currentState.status === 'paused') {
        return {
          ok: false,
          error: 'Digital seats cannot swap their library mid-game. Reset or finish the match first.',
          state: currentState,
          emittedEvents: [],
        };
      }

      const d = intent.deck;
      const instances: CardInstance[] = [];
      let idx = 1;
      const prefix = `${seat.seatId}_e${nextSeq}`;
      let mainCount = 0;
      let gameChangerCount = 0;

      for (const cmd of d.commanders) {
        if (cmd.isGameChanger || GAME_CHANGER_NAMES.has(cmd.name.toLowerCase())) gameChangerCount++;
        instances.push({
          instanceId: `${prefix}_cmd_${idx++}`,
          card: cmd,
          ownerSeatId: seat.seatId,
          controllerSeatId: seat.seatId,
          zone: currentState.settings.useCommandZone ? 'command' : 'library',
          tapped: false,
          faceDown: false,
          flipped: false,
          counters: {},
          attachedToId: null,
          exiledByInstanceId: null,
          row: classifyBattlefieldRow(cmd.typeLine),
          freePos: null,
          peekedBySeatIds: [],
          isToken: false,
          isCommander: true,
          enteredZoneSeq: nextSeq,
        });
      }

      for (const entry of d.mainboard) {
        const qty = Math.max(1, Math.min(99, entry.quantity));
        mainCount += qty;
        if (entry.card.isGameChanger || GAME_CHANGER_NAMES.has(entry.card.name.toLowerCase())) {
          gameChangerCount += qty;
        }
        for (let i = 0; i < qty; i++) {
          instances.push({
            instanceId: `${prefix}_c_${idx++}`,
            card: entry.card,
            ownerSeatId: seat.seatId,
            controllerSeatId: seat.seatId,
            zone: 'library',
            tapped: false,
            faceDown: false,
            flipped: false,
            counters: {},
            attachedToId: null,
            exiledByInstanceId: null,
            row: classifyBattlefieldRow(entry.card.typeLine),
            freePos: null,
            peekedBySeatIds: [],
            isToken: false,
            isCommander: false,
            enteredZoneSeq: nextSeq,
          });
        }
      }

      // Sideboard stays in 'sideboard' zone (NEVER library); Maybeboard stays out of play completely!
      let sideboardCount = 0;
      for (const entry of d.sideboard ?? []) {
        const qty = Math.max(1, Math.min(99, entry.quantity));
        sideboardCount += qty;
        for (let i = 0; i < qty; i++) {
          instances.push({
            instanceId: `${prefix}_sb_${idx++}`,
            card: entry.card,
            ownerSeatId: seat.seatId,
            controllerSeatId: seat.seatId,
            zone: 'sideboard',
            tapped: false,
            faceDown: false,
            flipped: false,
            counters: {},
            attachedToId: null,
            exiledByInstanceId: null,
            row: classifyBattlefieldRow(entry.card.typeLine),
            freePos: null,
            peekedBySeatIds: [],
            isToken: false,
            isCommander: false,
            enteredZoneSeq: nextSeq,
          });
        }
      }

      const maybeboardCount = (d.maybeboard ?? []).reduce((sum, e) => sum + e.quantity, 0);
      const detectedBracket: 1 | 2 | 3 | 4 | 5 =
        gameChangerCount >= 4 ? 4 : gameChangerCount >= 1 ? 3 : 2;
      const showTransparency = intent.showTransparency ?? true;
      const totalPlayDeck = mainCount + d.commanders.length;

      const colorIdentity = computeDeckColorIdentity(d);
      const legalCosmeticArt = buildLegalCosmeticArtPool(d, currentState.settings.format);

      if (
        intent.cosmetics &&
        ((intent.cosmetics.sleeveCardName && /^(https?:|data:|blob:)/i.test(intent.cosmetics.sleeveCardName.trim())) ||
          (intent.cosmetics.playmatCardName && /^(https?:|data:|blob:)/i.test(intent.cosmetics.playmatCardName.trim())))
      ) {
        return {
          ok: false,
          error: 'External image uploads/URLs are disabled in deck cosmetics.',
          state: currentState,
          emittedEvents: [],
        };
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'DECK_LOADED',
          `${actor.displayName} loaded deck "${d.name}" (${totalPlayDeck} cards${
            showTransparency ? `, ${gameChangerCount} Game Changers` : ''
          }).`,
          {
            seatId: seat.seatId,
            deckMeta: {
              deckId: d.deckId,
              name: d.name,
              mainCount,
              commanderNames: d.commanders.map((c) => c.name),
              colorIdentity,
              legalCosmeticArt,
              sideboardCount,
              maybeboardCount,
              gameChangerCount,
              detectedBracket,
              showTransparency,
            },
            availableTokens: extractTokensFromDeck(d),
            requestedCosmetics: intent.cosmetics
              ? {
                  sleeveCardName: intent.cosmetics.sleeveCardName,
                  playmatCardName: intent.cosmetics.playmatCardName,
                  focalPoint: intent.cosmetics.focalPoint,
                }
              : undefined,
          },
          { instances }
        )
      );
      break;
    }

    case 'LOAD_CUBE_POOL': {
      if (!hasAdminPower && Object.values(currentState.seats).filter((s) => s.accountId !== null).length > 1) {
        return { ok: false, error: 'Only the host can deal a table-wide Cube pool.', state: currentState, emittedEvents: [] };
      }
      if (currentState.status === 'active') {
        return { ok: false, error: 'Reset the match before dealing a new Cube pool.', state: currentState, emittedEvents: [] };
      }
      const seatedIds = currentState.seatOrder.filter((sid) => currentState.seats[sid]?.accountId !== null);
      if (seatedIds.length === 0) {
        return { ok: false, error: 'At least one player must be seated to deal a Cube pool.', state: currentState, emittedEvents: [] };
      }
      const totalCubeCards = intent.cube.mainboard.reduce((sum, e) => sum + e.quantity, 0);
      const cardsPerSeat =
        intent.mode === 'battlebox'
          ? Math.max(1, Math.floor(totalCubeCards / seatedIds.length))
          : Math.max(15, Math.min(90, intent.cardsPerSeat ?? 45));

      const dealtBySeat = dealCubePoolsToSeats(intent.cube, seatedIds, cardsPerSeat, rng);
      for (const sid of seatedIds) {
        const subDeck = dealtBySeat[sid];
        if (!subDeck) continue;
        const targetSeat = currentState.seats[sid];
        const instances: CardInstance[] = [];
        let idx = 1;
        const prefix = `${sid}_cube_${nextSeq}`;
        let mainCount = 0;
        let gameChangerCount = 0;
        for (const entry of subDeck.mainboard) {
          mainCount += entry.quantity;
          if (entry.card.isGameChanger || GAME_CHANGER_NAMES.has(entry.card.name.toLowerCase())) {
            gameChangerCount += entry.quantity;
          }
          for (let k = 0; k < entry.quantity; k++) {
            instances.push({
              instanceId: `${prefix}_c_${idx++}`,
              card: entry.card,
              ownerSeatId: sid,
              controllerSeatId: sid,
              zone: 'library',
              tapped: false,
              faceDown: false,
              flipped: false,
              counters: {},
              attachedToId: null,
              exiledByInstanceId: null,
              row: classifyBattlefieldRow(entry.card.typeLine),
              freePos: null,
              peekedBySeatIds: [],
              isToken: false,
              isCommander: false,
              enteredZoneSeq: nextSeq,
            });
          }
        }
        const colorIdentity = computeDeckColorIdentity(subDeck);
        const legalCosmeticArt = buildLegalCosmeticArtPool(subDeck, 'custom');
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'DECK_LOADED',
            `${actor.displayName} dealt ${mainCount} cards from Cube "${intent.cube.name}" (${intent.mode}) to ${targetSeat.displayName}.`,
            {
              seatId: sid,
              deckMeta: {
                name: `${intent.cube.name} (${intent.mode === 'battlebox' ? 'Battlebox' : 'Sealed'})`,
                mainCount,
                commanderNames: [],
                colorIdentity,
                legalCosmeticArt,
                sideboardCount: 0,
                maybeboardCount: 0,
                gameChangerCount,
                detectedBracket: 2,
                showTransparency: false,
              },
              availableTokens: extractTokensFromDeck(subDeck),
            },
            { instances }
          )
        );
      }
      break;
    }

    case 'START_MATCH': {
      if (!hasAdminPower && Object.values(currentState.seats).filter((s) => s.accountId !== null).length > 1) {
        return { ok: false, error: 'Only the host can start a multiplayer match.', state: currentState, emittedEvents: [] };
      }
      const seated = currentState.seatOrder.filter((sid) => currentState.seats[sid]?.accountId !== null);
      if (seated.length === 0) {
        return { ok: false, error: 'At least one player must be seated.', state: currentState, emittedEvents: [] };
      }

      const startingSeatId = seated[rng.nextInt(seated.length)];
      const perSeatLibraryOrder: Record<string, string[]> = {};
      const perSeatOpeningHands: Record<string, string[]> = {};

      for (const sid of seated) {
        const owned = Object.values(currentState.cards).filter(
          (c) =>
            c.ownerSeatId === sid &&
            !c.isToken &&
            c.zone !== 'sideboard' &&
            !(c.isCommander && currentState.settings.useCommandZone)
        );
        const ids = owned.map((c) => c.instanceId);
        const ordered = intent.autoShuffle === false ? ids : shuffleArrayWithRng(ids, rng);
        const handCount = Math.min(currentState.settings.openingHandSize, ordered.length);
        perSeatOpeningHands[sid] = ordered.slice(0, handCount);
        perSeatLibraryOrder[sid] = ordered;
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'MATCH_STARTED',
          `Match started! ${currentState.seats[startingSeatId]?.displayName ?? startingSeatId} goes first.`,
          { startingSeatId },
          { perSeatLibraryOrder, perSeatOpeningHands }
        )
      );
      break;
    }

    case 'RESET_MATCH': {
      if (!hasAdminPower && Object.values(currentState.seats).filter((s) => s.accountId !== null).length > 1) {
        return { ok: false, error: 'Only the host or judge can reset the match.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'MATCH_RESET',
          `${actor.displayName} reset the table for a new match.`,
          { startingSeatId: currentState.seatOrder[0] },
          { perSeatLibraryOrder: {}, perSeatOpeningHands: {} }
        )
      );
      break;
    }

    case 'ABANDON_MATCH': {
      if (!hasAdminPower) {
        return { ok: false, error: 'Only the host or judge can abandon the match.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'MATCH_ABANDONED', `${actor.displayName} ended the match.`, {})
      );
      break;
    }

    case 'MULLIGAN': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const nextMull = seat.mulliganCount + 1;
      const pool = [
        ...currentState.zoneOrder[seat.seatId].hand,
        ...currentState.zoneOrder[seat.seatId].library,
      ];
      const shuffled = shuffleArrayWithRng(pool, rng);
      const drawCount = Math.min(currentState.settings.openingHandSize, shuffled.length);
      const handIds = shuffled.slice(0, drawCount);
      const libraryOrder = shuffled.slice(drawCount);

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'MULLIGAN_TAKEN',
          `${actor.displayName} took mulligan #${nextMull} (drew ${drawCount}).`,
          { seatId: seat.seatId, mulliganCount: nextMull, drawnCount: drawCount },
          { handIds, libraryOrder }
        )
      );
      break;
    }

    case 'CONFIRM_OPENING_HAND': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const handSet = new Set(currentState.zoneOrder[seat.seatId].hand);
      const bottomIds = (intent.bottomCardIds ?? []).filter((id) => handSet.has(id));
      const keptCount = handSet.size - bottomIds.length;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'OPENING_HAND_CONFIRMED',
          `${actor.displayName} kept an opening hand of ${keptCount}${
            bottomIds.length > 0 ? ` (${bottomIds.length} placed on bottom)` : ''
          }.`,
          { seatId: seat.seatId, keptCount, bottomedCount: bottomIds.length },
          { bottomCardIds: bottomIds }
        )
      );
      break;
    }

    case 'MOVE_CARD': {
      const card = currentState.cards[intent.instanceId];
      if (!card) return { ok: false, error: 'Card not found.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== actor.seatId && card.ownerSeatId !== actor.seatId) {
        return { ok: false, error: 'You do not control or own that card.', state: currentState, emittedEvents: [] };
      }
      const fromZone = card.zone;
      const toZone = intent.toZone;
      const isPublicCard =
        !intent.faceDown &&
        (toZone === 'battlefield' || toZone === 'graveyard' || toZone === 'command' || toZone === 'stack' || toZone === 'revealed' || (toZone === 'exile' && !intent.faceDown));

      const cardLabel = isPublicCard ? card.card.name : 'a face-down card';
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARD_MOVED',
          `${actor.displayName} moved ${cardLabel} from ${fromZone} to ${toZone}.`,
          {
            instanceId: card.instanceId,
            fromZone,
            toZone,
            fromSeatId: card.controllerSeatId,
            toSeatId: intent.toSeatId ?? actor.seatId ?? card.ownerSeatId,
            position: intent.position ?? 'top',
            row: intent.row,
            freePos: intent.freePos,
            faceDown: Boolean(intent.faceDown),
            tapped: Boolean(intent.tapped),
            exiledByInstanceId: intent.exiledByInstanceId ?? null,
            publicCardName: isPublicCard ? card.card.name : null,
            publicCardImage: isPublicCard ? card.card.imageUrl : null,
          }
        )
      );
      break;
    }

    case 'TAP_CARD': {
      const card = currentState.cards[intent.instanceId];
      if (!card) return { ok: false, error: 'Card not found.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== actor.seatId) {
        return { ok: false, error: 'You do not control that permanent.', state: currentState, emittedEvents: [] };
      }
      const nextTapped = intent.tapped ?? !card.tapped;
      const label = card.faceDown ? 'a face-down permanent' : card.card.name;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARD_TAPPED',
          `${actor.displayName} ${nextTapped ? 'tapped' : 'untapped'} ${label}.`,
          { instanceId: card.instanceId, tapped: nextTapped }
        )
      );
      break;
    }

    case 'UNTAP_ALL': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'ALL_UNTAPPED', `${actor.displayName} untapped all permanents.`, {
          seatId: seat.seatId,
        })
      );
      break;
    }

    case 'FLIP_CARD': {
      const card = currentState.cards[intent.instanceId];
      if (!card) return { ok: false, error: 'Card not found.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== actor.seatId && card.ownerSeatId !== actor.seatId) {
        return { ok: false, error: 'Unauthorized.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CARD_FLIPPED', `${actor.displayName} transformed ${card.card.name}.`, {
          instanceId: card.instanceId,
        })
      );
      break;
    }

    case 'TOGGLE_FACE_DOWN': {
      const card = currentState.cards[intent.instanceId];
      if (!card) return { ok: false, error: 'Card not found.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== actor.seatId) {
        return { ok: false, error: 'Unauthorized.', state: currentState, emittedEvents: [] };
      }
      const becomingFaceUp = card.faceDown;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARD_FACE_DOWN_TOGGLED',
          becomingFaceUp
            ? `${actor.displayName} turned ${card.card.name} face up!`
            : `${actor.displayName} turned ${card.card.name} face down.`,
          { instanceId: card.instanceId }
        )
      );
      break;
    }

    case 'PEEK_FACE_DOWN': {
      const card = currentState.cards[intent.instanceId];
      if (!card || !seat) return { ok: false, error: 'Invalid card or seat.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== seat.seatId && card.ownerSeatId !== seat.seatId) {
        return { ok: false, error: 'Only the controller or owner may peek.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CARD_PEEKED', `${actor.displayName} peeked at a face-down card.`, {
          instanceId: card.instanceId,
          seatId: seat.seatId,
        })
      );
      break;
    }

    case 'ATTACH_CARD': {
      const card = currentState.cards[intent.instanceId];
      if (!card) return { ok: false, error: 'Card not found.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== actor.seatId) {
        return { ok: false, error: 'Unauthorized.', state: currentState, emittedEvents: [] };
      }
      const target = intent.targetInstanceId ? currentState.cards[intent.targetInstanceId] : null;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARD_ATTACHED',
          target
            ? `${actor.displayName} attached ${card.card.name} to ${target.faceDown ? 'a face-down permanent' : target.card.name}.`
            : `${actor.displayName} unattached ${card.card.name}.`,
          { instanceId: card.instanceId, targetInstanceId: intent.targetInstanceId }
        )
      );
      break;
    }

    case 'CHANGE_CONTROLLER': {
      const card = currentState.cards[intent.instanceId];
      const newSeat = currentState.seats[intent.newControllerSeatId];
      if (!card || !newSeat) return { ok: false, error: 'Invalid target.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && card.controllerSeatId !== actor.seatId) {
        return { ok: false, error: 'Only the current controller can give control.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARD_CONTROLLER_CHANGED',
          `${actor.displayName} gave control of ${card.faceDown ? 'a face-down permanent' : card.card.name} to ${newSeat.displayName}.`,
          { instanceId: card.instanceId, newControllerSeatId: intent.newControllerSeatId }
        )
      );
      break;
    }

    case 'ADJUST_CARD_COUNTER': {
      const card = currentState.cards[intent.instanceId];
      if (!card) return { ok: false, error: 'Card not found.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARD_COUNTER_ADJUSTED',
          `${actor.displayName} ${intent.delta >= 0 ? 'added' : 'removed'} ${Math.abs(intent.delta)} ${intent.counter} counter on ${
            card.faceDown ? 'a face-down card' : card.card.name
          }.`,
          { instanceId: card.instanceId, counter: intent.counter, delta: intent.delta }
        )
      );
      break;
    }

    case 'CREATE_TOKEN': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const count = Math.max(1, Math.min(25, intent.count));
      const source = intent.copyOfInstanceId ? currentState.cards[intent.copyOfInstanceId]?.card : null;
      const instances: CardInstance[] = [];
      for (let i = 0; i < count; i++) {
        const def = source
          ? { ...source, name: `${source.name} (Token)` }
          : {
              scryfallId: `token:${intent.token.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
              name: intent.token.name,
              manaCost: '',
              cmc: 0,
              typeLine: intent.token.typeLine,
              oracleText: intent.token.oracleText ?? '',
              colors: intent.token.colors ?? [],
              power: intent.token.power,
              toughness: intent.token.toughness,
              imageUrl:
                intent.token.imageUrl ??
                `https://api.scryfall.com/cards/named?format=image&version=normal&fuzzy=${encodeURIComponent(intent.token.name)}`,
              layout: 'normal' as const,
            };
        instances.push({
          instanceId: `${seat.seatId}_tok_${nextSeq}_${i + 1}`,
          card: def,
          ownerSeatId: seat.seatId,
          controllerSeatId: seat.seatId,
          zone: 'battlefield',
          tapped: false,
          faceDown: false,
          flipped: false,
          counters: {},
          attachedToId: null,
          exiledByInstanceId: null,
          row: classifyBattlefieldRow(def.typeLine),
          freePos: null,
          peekedBySeatIds: [],
          isToken: true,
          isCommander: false,
          enteredZoneSeq: nextSeq,
        });
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'TOKENS_CREATED',
          `${actor.displayName} created ${count}× ${instances[0].card.name}.`,
          { seatId: seat.seatId, count, tokenName: instances[0].card.name },
          { instances }
        )
      );
      break;
    }

    case 'DRAW_CARDS': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const lib = currentState.zoneOrder[seat.seatId].library;
      const count = Math.max(1, Math.min(20, intent.count));
      if (lib.length === 0) {
        return { ok: false, error: 'Library is empty!', state: currentState, emittedEvents: [] };
      }
      const drawnIds = intent.fromBottom ? lib.slice(-count).reverse() : lib.slice(0, count);
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARDS_DRAWN',
          `${actor.displayName} drew ${drawnIds.length} card${drawnIds.length === 1 ? '' : 's'}${
            intent.fromBottom ? ' from the bottom of their library' : ''
          }.`,
          { seatId: seat.seatId, count: drawnIds.length, fromBottom: Boolean(intent.fromBottom) },
          { drawnIds }
        )
      );
      break;
    }

    case 'MILL_CARDS': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const lib = currentState.zoneOrder[seat.seatId].library;
      const count = Math.max(1, Math.min(30, intent.count));
      const milledIds = lib.slice(0, count);
      if (milledIds.length === 0) {
        return { ok: false, error: 'Library is empty!', state: currentState, emittedEvents: [] };
      }
      const names = milledIds.map((id) => currentState.cards[id]?.card.name).filter(Boolean);
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARDS_MILLED',
          `${actor.displayName} milled ${milledIds.length} (${names.join(', ')}).`,
          { seatId: seat.seatId, milledIds }
        )
      );
      break;
    }

    case 'SCRY_START':
    case 'LOOK_TOP_START': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const lib = currentState.zoneOrder[seat.seatId].library;
      const count = Math.max(1, Math.min(15, intent.count, lib.length));
      if (count === 0) return { ok: false, error: 'Library is empty.', state: currentState, emittedEvents: [] };
      const kind = intent.type === 'SCRY_START' ? 'scry' : 'look_top';
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PRIVATE_LOOK_STARTED',
          kind === 'scry' ? `${actor.displayName} is scrying ${count}...` : `${actor.displayName} is looking at the top ${count} card(s)...`,
          { seatId: seat.seatId, kind, count },
          { cardInstanceIds: lib.slice(0, count) }
        )
      );
      break;
    }

    case 'SCRY_RESOLVE': {
      if (!seat || !seat.privateLook) {
        return { ok: false, error: 'No active scry/look session.', state: currentState, emittedEvents: [] };
      }
      const allowed = new Set(seat.privateLook.cardInstanceIds);
      const topIds = intent.topIds.filter((id) => allowed.has(id));
      const bottomIds = intent.bottomIds.filter((id) => allowed.has(id) && !topIds.includes(id));
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'SCRY_RESOLVED',
          `${actor.displayName} finished scrying (${topIds.length} top, ${bottomIds.length} bottom).`,
          { seatId: seat.seatId, topCount: topIds.length, bottomCount: bottomIds.length },
          { topIds, bottomIds }
        )
      );
      break;
    }

    case 'SEARCH_LIBRARY_START': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const lib = currentState.zoneOrder[seat.seatId].library;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PRIVATE_LOOK_STARTED',
          `${actor.displayName} is searching their library (${lib.length} cards)...`,
          { seatId: seat.seatId, kind: 'search_library', count: lib.length },
          { cardInstanceIds: [...lib] }
        )
      );
      break;
    }

    case 'SEARCH_LIBRARY_PICK': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const card = currentState.cards[intent.instanceId];
      if (!card || card.ownerSeatId !== seat.seatId || card.zone !== 'library') {
        return { ok: false, error: 'Card is not in your library.', state: currentState, emittedEvents: [] };
      }
      const destZone: ZoneId = intent.destination === 'top' ? 'library' : intent.destination;
      const isPublic = destZone === 'battlefield' && !intent.faceDown;
      if (intent.destination === 'top') {
        const remaining = currentState.zoneOrder[seat.seatId].library.filter((id) => id !== card.instanceId);
        const reordered = intent.shuffleAfter ? [card.instanceId, ...shuffleArrayWithRng(remaining, rng)] : [card.instanceId, ...remaining];
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'LIBRARY_SHUFFLED',
            `${actor.displayName} tutored a card to the top of their library${intent.shuffleAfter ? ' after shuffling' : ''}.`,
            { seatId: seat.seatId },
            { newOrder: reordered }
          )
        );
      } else {
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'CARD_MOVED',
            `${actor.displayName} tutored ${isPublic ? card.card.name : 'a card'} from library to ${destZone}.`,
            {
              instanceId: card.instanceId,
              fromZone: 'library',
              toZone: destZone,
              fromSeatId: seat.seatId,
              toSeatId: seat.seatId,
              position: 'top',
              faceDown: Boolean(intent.faceDown),
              publicCardName: isPublic ? card.card.name : null,
              publicCardImage: isPublic ? card.card.imageUrl : null,
            }
          )
        );
        if (intent.shuffleAfter) {
          const remaining = currentState.zoneOrder[seat.seatId].library.filter((id) => id !== card.instanceId);
          events.push(
            makeEvent(
              currentState,
              nextSeq++,
              now,
              actor,
              'LIBRARY_SHUFFLED',
              `${actor.displayName} shuffled their library.`,
              { seatId: seat.seatId },
              { newOrder: shuffleArrayWithRng(remaining, rng) }
            )
          );
        }
        events.push(
          makeEvent(currentState, nextSeq++, now, actor, 'PRIVATE_LOOK_CLOSED', `${actor.displayName} closed library search.`, {
            seatId: seat.seatId,
          })
        );
      }
      break;
    }

    case 'CLOSE_PRIVATE_LOOK': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'PRIVATE_LOOK_CLOSED', `${actor.displayName} finished looking at library.`, {
          seatId: seat.seatId,
        })
      );
      if (intent.shuffleAfter) {
        const lib = currentState.zoneOrder[seat.seatId].library;
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'LIBRARY_SHUFFLED',
            `${actor.displayName} shuffled their library.`,
            { seatId: seat.seatId },
            { newOrder: shuffleArrayWithRng(lib, rng) }
          )
        );
      }
      break;
    }

    case 'REVEAL_TOP_CARD': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const topId = currentState.zoneOrder[seat.seatId].library[0];
      if (!topId) return { ok: false, error: 'Library is empty.', state: currentState, emittedEvents: [] };
      const topCard = currentState.cards[topId];
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'TOP_CARD_REVEALED',
          `${actor.displayName} revealed the top card of their library: ${topCard.card.name}!`,
          { seatId: seat.seatId, instanceId: topId, cardName: topCard.card.name, cardImage: topCard.card.imageUrl }
        )
      );
      break;
    }

    case 'SHUFFLE_LIBRARY': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const lib = currentState.zoneOrder[seat.seatId].library;
      const newOrder = shuffleArrayWithRng(lib, rng);
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'LIBRARY_SHUFFLED',
          `${actor.displayName} shuffled their library (${lib.length} cards).`,
          { seatId: seat.seatId, count: lib.length },
          { newOrder }
        )
      );
      break;
    }

    case 'TOGGLE_PLAY_TOP_REVEALED': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PLAY_TOP_REVEALED_TOGGLED',
          `${actor.displayName} ${intent.enabled ? 'is now playing with top of library revealed' : 'hid top of library'}.`,
          { seatId: seat.seatId, enabled: intent.enabled }
        )
      );
      break;
    }

    case 'ADJUST_LIFE': {
      const targetSeatId = intent.targetSeatId ?? actor.seatId;
      if (!targetSeatId || !currentState.seats[targetSeatId]) {
        return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      }
      const target = currentState.seats[targetSeatId];
      const nextLife = target.life + intent.delta;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'LIFE_ADJUSTED',
          `${target.displayName} life ${intent.delta >= 0 ? `+${intent.delta}` : intent.delta} (${target.life} → ${nextLife}).`,
          { targetSeatId, delta: intent.delta, newLife: nextLife }
        )
      );
      break;
    }

    case 'ADJUST_PLAYER_COUNTER': {
      const targetSeatId = intent.targetSeatId ?? actor.seatId;
      if (!targetSeatId || !currentState.seats[targetSeatId]) {
        return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      }
      const target = currentState.seats[targetSeatId];
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PLAYER_COUNTER_ADJUSTED',
          `${target.displayName} ${intent.counter} ${intent.delta >= 0 ? `+${intent.delta}` : intent.delta}.`,
          { targetSeatId, counter: intent.counter, delta: intent.delta }
        )
      );
      break;
    }

    case 'ADJUST_COMMANDER_DAMAGE': {
      const target = currentState.seats[intent.targetSeatId];
      if (!target) return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'COMMANDER_DAMAGE_ADJUSTED',
          `${target.displayName} took ${intent.delta >= 0 ? `+${intent.delta}` : intent.delta} commander damage.`,
          {
            targetSeatId: intent.targetSeatId,
            sourceSeatId: intent.sourceSeatId,
            commanderInstanceId: intent.commanderInstanceId,
            delta: intent.delta,
          }
        )
      );
      break;
    }

    case 'ADJUST_COMMANDER_TAX': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'COMMANDER_TAX_ADJUSTED', `${actor.displayName} adjusted commander tax.`, {
          seatId: seat.seatId,
          commanderInstanceId: intent.commanderInstanceId,
          delta: intent.delta,
        })
      );
      break;
    }

    case 'SET_DESIGNATION': {
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'DESIGNATION_SET',
          `${actor.displayName} updated ${intent.designation}.`,
          { designation: intent.designation, seatId: intent.seatId ?? actor.seatId }
        )
      );
      break;
    }

    case 'SET_PHASE':
    case 'ADVANCE_PHASE': {
      if (!hasAdminPower && !isSeatOnActiveTurn(currentState, actor.seatId)) {
        return { ok: false, error: 'Only the active player (or active teammate) can advance the phase.', state: currentState, emittedEvents: [] };
      }
      if (currentState.flags.length > 0 && currentState.settings.flagGateMode === 'phase_and_turn') {
        return {
          ok: false,
          error: `Resolve ${currentState.flags.length} interaction flag(s) before advancing phase.`,
          openFlags: currentState.flags,
          state: currentState,
          emittedEvents: [],
        };
      }
      let nextPhase: GamePhase;
      if (intent.type === 'SET_PHASE') {
        nextPhase = intent.phase;
      } else {
        const curIdx = GAME_PHASES.findIndex((p) => p.id === currentState.phase);
        nextPhase = GAME_PHASES[(curIdx + 1) % GAME_PHASES.length].id;
      }
      const label = GAME_PHASES.find((p) => p.id === nextPhase)?.label ?? nextPhase;
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'PHASE_CHANGED', `Phase → ${label}.`, { phase: nextPhase })
      );

      // Auto-raise priority flags for opponents who configured a stop preset on `nextPhase`
      const activeSeats = getActiveSeatsInOrder(currentState);
      for (const sid of activeSeats) {
        if (isSeatOnActiveTurn(currentState, sid)) continue;
        const opp = currentState.seats[sid];
        if (
          opp &&
          !opp.flagMuted &&
          opp.connected &&
          opp.stopPresets.includes(nextPhase) &&
          !currentState.flags.some((f) => f.seatId === sid)
        ) {
          const flagId = `flag_${nextSeq}`;
          events.push(
            makeEvent(
              currentState,
              nextSeq++,
              now,
              {
                accountId: opp.accountId ?? '',
                seatId: sid,
                isHost: false,
                isJudge: false,
                displayName: opp.displayName,
                publicId: opp.publicId ?? '',
              },
              'FLAG_RAISED',
              `${opp.displayName} auto-stopped on ${label} (Stop Preset).`,
              { flagId, seatId: sid, reason: 'respond' }
            )
          );
        }
      }
      break;
    }

    case 'PASS_TURN': {
      if (!hasAdminPower && !isSeatOnActiveTurn(currentState, actor.seatId)) {
        return { ok: false, error: 'Only the active player (or active teammate) can pass the turn.', state: currentState, emittedEvents: [] };
      }
      if (currentState.flags.length > 0) {
        return {
          ok: false,
          error: `Resolve ${currentState.flags.length} open flag(s) before passing turn.`,
          openFlags: currentState.flags,
          state: currentState,
          emittedEvents: [],
        };
      }
      const activeSeats = getActiveSeatsInOrder(currentState);
      const len = Math.max(1, activeSeats.length);
      const nextExtraTurnsQueue = [...currentState.extraTurnsQueue];
      const nextSkipTurnSeatIds = [...currentState.skipTurnSeatIds];

      let nextSeatId = currentState.activeSeatId;
      let nextTurnNumber = currentState.turnNumber;

      if (nextExtraTurnsQueue.length > 0) {
        const queuedSeat = nextExtraTurnsQueue.shift()!;
        if (activeSeats.includes(queuedSeat)) {
          nextSeatId = queuedSeat;
          nextTurnNumber = currentState.turnNumber + 1;
        }
      }

      if (nextSeatId === currentState.activeSeatId && nextExtraTurnsQueue.length === 0 && currentState.extraTurnsQueue.length === 0) {
        let curIdx = Math.max(0, activeSeats.indexOf(currentState.activeSeatId));
        let wrapped = false;
        const activeTeamId =
          currentState.settings.teamMode && currentState.settings.simultaneousTeamTurns
            ? currentState.seats[currentState.activeSeatId]?.teamId ?? null
            : null;

        for (let guard = 0; guard < len * 2; guard++) {
          const candidateIdx = (curIdx + currentState.turnDirection + len) % len;
          if (
            len === 1 ||
            (currentState.turnDirection === 1 && candidateIdx === 0) ||
            (currentState.turnDirection === -1 && candidateIdx === len - 1)
          ) {
            wrapped = true;
          }
          curIdx = candidateIdx;
          const candidateSid = activeSeats[curIdx] ?? currentState.activeSeatId;

          // In 2HG simultaneous team turns, advance past same-team partner to the next opposing team
          if (activeTeamId && currentState.seats[candidateSid]?.teamId === activeTeamId && len > 2) {
            continue;
          }

          // Consume queued Skip Next Turn if present
          const skipPos = nextSkipTurnSeatIds.indexOf(candidateSid);
          if (skipPos !== -1 && len > 1) {
            nextSkipTurnSeatIds.splice(skipPos, 1);
            continue;
          }

          nextSeatId = candidateSid;
          break;
        }
        nextTurnNumber = wrapped ? currentState.turnNumber + 1 : currentState.turnNumber;
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'TURN_PASSED',
          `${actor.displayName} passed turn to ${currentState.seats[nextSeatId]?.displayName ?? nextSeatId} (Turn ${nextTurnNumber}).`,
          { nextSeatId, nextTurnNumber, nextExtraTurnsQueue, nextSkipTurnSeatIds }
        )
      );
      break;
    }

    case 'UNDO_PASS_TURN': {
      if (!currentState.lastTurnSnapshot) {
        return { ok: false, error: 'No recent pass turn to undo.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'PASS_TURN_UNDONE', `${actor.displayName} undid Pass Turn.`, {})
      );
      break;
    }

    case 'REVERSE_TURN_DIRECTION': {
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'TURN_DIRECTION_REVERSED', `${actor.displayName} reversed turn order!`, {})
      );
      break;
    }

    case 'RAISE_FLAG': {
      if (!seat || seat.status === 'eliminated') {
        return { ok: false, error: 'Only active seated players can raise an interaction flag.', state: currentState, emittedEvents: [] };
      }
      if (seat.flagMuted) {
        return { ok: false, error: 'Your interaction flag has been muted by the table host.', state: currentState, emittedEvents: [] };
      }
      if (seat.lastFlagClearedAt !== null && now - seat.lastFlagClearedAt < 3_000) {
        return { ok: false, error: 'Flag cooldown active (wait 3s before re-raising).', state: currentState, emittedEvents: [] };
      }
      if (currentState.flags.some((f) => f.seatId === seat.seatId)) {
        return { ok: false, error: 'You already have an open flag.', state: currentState, emittedEvents: [] };
      }
      const flagId = `flag_${nextSeq}`;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'FLAG_RAISED',
          `${actor.displayName} raised a Wait flag (${intent.reason}).`,
          { flagId, seatId: seat.seatId, reason: intent.reason }
        )
      );
      break;
    }

    case 'MARK_FLAG_SEEN': {
      const flag = currentState.flags.find((f) => f.flagId === intent.flagId);
      if (!flag) return { ok: false, error: 'Flag not found.', state: currentState, emittedEvents: [] };
      if (!hasAdminPower && !isSeatOnActiveTurn(currentState, actor.seatId)) {
        return { ok: false, error: 'Only the active player or host marks flags seen.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'FLAG_SEEN', `${actor.displayName} acknowledged ${currentState.seats[flag.seatId]?.displayName}'s flag.`, {
          flagId: flag.flagId,
        })
      );
      break;
    }

    case 'CLEAR_FLAG': {
      const flag = currentState.flags.find((f) => f.flagId === intent.flagId);
      if (!flag) return { ok: false, error: 'Flag not found.', state: currentState, emittedEvents: [] };
      const raiserSeat = currentState.seats[flag.seatId];
      const isRaiser = actor.seatId === flag.seatId;
      const isStaleAway =
        isSeatOnActiveTurn(currentState, actor.seatId) &&
        raiserSeat &&
        (!raiserSeat.connected || raiserSeat.status === 'afk' || raiserSeat.status === 'brb') &&
        now - flag.createdAt >= 30_000;

      if (!isRaiser && !hasAdminPower && !isStaleAway) {
        return {
          ok: false,
          error: 'Only the flag raiser, the host/judge, or the active player (after 30s if away) can clear a flag.',
          state: currentState,
          emittedEvents: [],
        };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'FLAG_CLEARED', `${actor.displayName} cleared interaction flag.`, {
          flagId: flag.flagId,
        })
      );
      break;
    }

    case 'ELIMINATE_PLAYER': {
      const target = currentState.seats[intent.targetSeatId];
      if (!target || target.status === 'eliminated') {
        return { ok: false, error: 'Player already eliminated or empty.', state: currentState, emittedEvents: [] };
      }
      if (!hasAdminPower && actor.seatId !== intent.targetSeatId) {
        return { ok: false, error: 'Only the player themselves, host, or judge can confirm elimination.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PLAYER_ELIMINATED',
          `${target.displayName} was eliminated (${intent.reason}) on Turn ${currentState.turnNumber}!`,
          {
            targetSeatId: intent.targetSeatId,
            reason: intent.reason,
            eliminatedBySeatId: intent.eliminatedBySeatId ?? null,
          }
        )
      );
      break;
    }

    case 'UNDO_LAST_ACTION': {
      if (eventLog.length === 0) {
        return { ok: false, error: 'Nothing to undo.', state: currentState, emittedEvents: [] };
      }
      const last = eventLog[eventLog.length - 1];
      if (!hasAdminPower && last.actorSeatId !== actor.seatId) {
        return {
          ok: false,
          error: 'Another player acted since your move — ask the host/table to Rollback instead.',
          state: currentState,
          emittedEvents: [],
        };
      }
      const truncated = eventLog.slice(0, -1);
      const rebuilt = replayEvents(initialSnapshot, truncated);
      return {
        ok: true,
        state: rebuilt,
        emittedEvents: [],
        replacedEventLog: [...truncated],
      };
    }

    case 'ROLLBACK_TO_SEQ': {
      if (!hasAdminPower && Object.values(currentState.seats).filter((s) => s.accountId !== null).length > 1) {
        return { ok: false, error: 'Host or majority approval required for rollback.', state: currentState, emittedEvents: [] };
      }
      const truncated = eventLog.filter((e) => e.seq <= intent.targetSeq);
      const rebuilt = replayEvents(initialSnapshot, truncated);
      return {
        ok: true,
        state: rebuilt,
        emittedEvents: [],
        replacedEventLog: [...truncated],
      };
    }

    case 'ROLL_DIE': {
      const sides = Math.max(2, Math.min(100, intent.sides));
      const result = rng.nextInt(sides) + 1;
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'DIE_ROLLED', `${actor.displayName} rolled a d${sides}: ${result}!`, {
          sides,
          result,
        })
      );
      break;
    }

    case 'FLIP_COIN': {
      const heads = rng.nextInt(2) === 0;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'COIN_FLIPPED',
          `${actor.displayName} flipped a coin: ${heads ? 'HEADS' : 'TAILS'}!`,
          { result: heads ? 'heads' : 'tails' }
        )
      );
      break;
    }

    case 'UPDATE_SHARED_NOTES': {
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'SHARED_NOTES_UPDATED', `${actor.displayName} updated table notes.`, {
          notes: intent.notes,
        })
      );
      break;
    }

    case 'SEND_CHAT': {
      if (!isSeatedPlayer && currentState.settings.allowSpectatorChat === false && !hasAdminPower) {
        return { ok: false, error: 'Spectator chat is disabled at this table.', state: currentState, emittedEvents: [] };
      }
      const text = intent.text.trim().slice(0, 500);
      if (!text) return { ok: false, error: 'Empty message.', state: currentState, emittedEvents: [] };
      const channel = !isSeatedPlayer ? 'spectator' : intent.channel ?? 'table';
      const whisperToSeatId = isSeatedPlayer ? (intent.whisperToSeatId ?? null) : null;
      const targetName = whisperToSeatId
        ? currentState.seats[whisperToSeatId]?.displayName ?? whisperToSeatId
        : null;
      const message = {
        id: `msg_${nextSeq}`,
        channel,
        senderPublicId: actor.publicId,
        senderName: actor.displayName,
        seatId: isSeatedPlayer ? actor.seatId : null,
        text,
        whisperToSeatId,
        replyToId: intent.replyToId ?? null,
        reactions: {},
        editedAt: null,
        deleted: false,
        createdAt: now,
      };
      const summary = whisperToSeatId
        ? `${actor.displayName} whispered to ${targetName}.`
        : `${actor.displayName}: ${text}`;
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CHAT_MESSAGE_SENT', summary, {
          message,
        })
      );
      break;
    }

    case 'EDIT_CHAT': {
      const msg = currentState.chat.find((m) => m.id === intent.messageId);
      if (!msg || msg.deleted) {
        return { ok: false, error: 'Message not found.', state: currentState, emittedEvents: [] };
      }
      if (msg.senderPublicId !== actor.publicId) {
        return { ok: false, error: 'You can only edit your own chat messages.', state: currentState, emittedEvents: [] };
      }
      const newText = intent.newText.trim().slice(0, 500);
      if (!newText) return { ok: false, error: 'Edited message cannot be empty.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CHAT_MESSAGE_EDITED', `${actor.displayName} edited a chat message.`, {
          messageId: msg.id,
          newText,
        })
      );
      break;
    }

    case 'DELETE_CHAT': {
      const msg = currentState.chat.find((m) => m.id === intent.messageId);
      if (!msg || msg.deleted) {
        return { ok: false, error: 'Message not found.', state: currentState, emittedEvents: [] };
      }
      if (msg.senderPublicId !== actor.publicId && !hasAdminPower) {
        return { ok: false, error: 'Only the sender or host/judge may delete this message.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CHAT_MESSAGE_DELETED', `${actor.displayName} deleted a chat message.`, {
          messageId: msg.id,
        })
      );
      break;
    }

    case 'REACT_CHAT': {
      const msg = currentState.chat.find((m) => m.id === intent.messageId);
      const emoji = intent.emoji.trim().slice(0, 8);
      if (!msg || msg.deleted || !emoji) {
        return { ok: false, error: 'Cannot react to message.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CHAT_REACTION_TOGGLED', `${actor.displayName} reacted ${emoji}.`, {
          messageId: msg.id,
          emoji,
          actorPublicId: actor.publicId,
        })
      );
      break;
    }

    case 'PAUSE_SAVE_MATCH': {
      const activeHumanCount = Object.values(currentState.seats).filter((s) => s.accountId !== null).length;
      if (!hasAdminPower && activeHumanCount > 1) {
        return { ok: false, error: 'Only the host or judge can pause and save a multiplayer pod.', state: currentState, emittedEvents: [] };
      }
      if (currentState.status !== 'active') {
        return { ok: false, error: 'Match must be active to pause and save.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'MATCH_PAUSED_SAVED', `${actor.displayName} paused and saved the match.`, {})
      );
      break;
    }

    case 'RESUME_SAVED_MATCH': {
      const activeHumanCount = Object.values(currentState.seats).filter((s) => s.accountId !== null).length;
      if (!hasAdminPower && activeHumanCount > 1) {
        return { ok: false, error: 'Only the host or judge can resume a saved pod.', state: currentState, emittedEvents: [] };
      }
      if (currentState.status !== 'paused') {
        return { ok: false, error: 'Match is not paused.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'MATCH_RESUMED', `${actor.displayName} resumed the saved match!`, {})
      );
      break;
    }

    case 'TOGGLE_POSTGAME_HAND_REVEAL': {
      if (!seat || seat.accountId !== actor.accountId) {
        return { ok: false, error: 'You must own a seat to toggle post-game hand reveal.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'POSTGAME_HAND_REVEAL_TOGGLED',
          `${actor.displayName} ${intent.enabled ? 'shared' : 'hid'} their post-game hand.`,
          { seatId: seat.seatId, enabled: intent.enabled }
        )
      );
      break;
    }

    case 'INSPECT_CARD': {
      if (seat?.hideClickedCards || currentState.settings.competitiveMode) {
        return { ok: true, state: currentState, emittedEvents: [] };
      }
      const card = currentState.cards[intent.instanceId];
      if (!card || card.faceDown) return { ok: true, state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'CARD_INSPECTED', `${actor.displayName} inspected ${card.card.name}.`, {
          instanceId: card.instanceId,
          cardName: card.card.name,
        })
      );
      break;
    }

    case 'REORDER_SEATS': {
      if (!hasAdminPower) {
        return { ok: false, error: 'Only the host or judge can reorder seats.', state: currentState, emittedEvents: [] };
      }
      const curSet = new Set(currentState.seatOrder);
      if (
        intent.newSeatOrder.length !== currentState.seatOrder.length ||
        intent.newSeatOrder.some((id) => !curSet.has(id)) ||
        new Set(intent.newSeatOrder).size !== curSet.size
      ) {
        return { ok: false, error: 'Invalid seat order permutation.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(currentState, nextSeq++, now, actor, 'SEATS_REORDERED', `${actor.displayName} updated table seat order.`, {
          newSeatOrder: intent.newSeatOrder,
        })
      );
      break;
    }

    case 'ASSIGN_TEAM': {
      if (!hasAdminPower && actor.seatId !== intent.targetSeatId) {
        return { ok: false, error: 'Only the player themselves or host can assign teams.', state: currentState, emittedEvents: [] };
      }
      const target = currentState.seats[intent.targetSeatId];
      if (!target) return { ok: false, error: 'Seat not found.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'TEAM_ASSIGNED',
          `${target.displayName} assigned to ${intent.teamId ?? 'Free-for-All'}.`,
          { targetSeatId: intent.targetSeatId, teamId: intent.teamId }
        )
      );
      break;
    }

    case 'ADD_EXTRA_TURN': {
      const targetSid = intent.targetSeatId ?? actor.seatId ?? currentState.activeSeatId;
      const target = currentState.seats[targetSid];
      if (!target || target.status === 'eliminated') {
        return { ok: false, error: 'Valid active target seat required for extra turn.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'EXTRA_TURN_ADDED',
          `${target.displayName} takes an extra turn after this one!`,
          { targetSeatId: targetSid }
        )
      );
      break;
    }

    case 'SKIP_NEXT_TURN': {
      const target = currentState.seats[intent.targetSeatId];
      if (!target || target.status === 'eliminated') {
        return { ok: false, error: 'Valid active target seat required.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'SKIP_TURN_ADDED',
          `${target.displayName} will skip their next turn.`,
          { targetSeatId: intent.targetSeatId }
        )
      );
      break;
    }

    case 'VOTE_AWAY_PLAYER': {
      if (!seat) return { ok: false, error: 'Only seated players can vote.', state: currentState, emittedEvents: [] };
      const target = currentState.seats[intent.targetSeatId];
      if (!target || target.status === 'eliminated' || target.accountId === null) {
        return { ok: false, error: 'Target seat is empty or already eliminated.', state: currentState, emittedEvents: [] };
      }
      const isAway = !target.connected || target.status === 'afk' || target.status === 'brb';
      if (!isAway && !hasAdminPower) {
        return { ok: false, error: 'Player is currently connected and active.', state: currentState, emittedEvents: [] };
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'AWAY_VOTE_RECORDED',
          `${actor.displayName} voted to ${intent.action === 'remove' ? 'remove' : 'skip turn of'} away player ${target.displayName}.`,
          { targetSeatId: intent.targetSeatId, action: intent.action, voterSeatId: seat.seatId }
        )
      );

      // Compute whether majority of other active connected seats reached
      const eligibleVoters = getActiveSeatsInOrder(currentState).filter(
        (sid) => sid !== intent.targetSeatId && currentState.seats[sid]?.connected
      );
      const existingVoters =
        currentState.awayVotes[intent.targetSeatId]?.action === intent.action
          ? currentState.awayVotes[intent.targetSeatId].voterSeatIds
          : [];
      const combinedCount = new Set([...existingVoters, seat.seatId]).size;
      const needed = Math.max(1, Math.ceil(eligibleVoters.length / 2));

      if (combinedCount >= needed) {
        if (intent.action === 'remove') {
          events.push(
            makeEvent(
              currentState,
              nextSeq++,
              now,
              actor,
              'PLAYER_ELIMINATED',
              `Table voted (${combinedCount}/${eligibleVoters.length}) to remove away player ${target.displayName}.`,
              { targetSeatId: intent.targetSeatId, reason: 'table', eliminatedBySeatId: null }
            )
          );
        } else if (currentState.activeSeatId === intent.targetSeatId) {
          const activeSeats = getActiveSeatsInOrder(currentState);
          const len = Math.max(1, activeSeats.length);
          const curIdx = Math.max(0, activeSeats.indexOf(currentState.activeSeatId));
          const nextIdx = (curIdx + currentState.turnDirection + len) % len;
          const nextSeatId = activeSeats[nextIdx] ?? currentState.activeSeatId;
          events.push(
            makeEvent(
              currentState,
              nextSeq++,
              now,
              actor,
              'TURN_PASSED',
              `Table voted to skip away player ${target.displayName}'s turn.`,
              { nextSeatId, nextTurnNumber: currentState.turnNumber + 1 }
            )
          );
        } else {
          events.push(
            makeEvent(
              currentState,
              nextSeq++,
              now,
              actor,
              'SKIP_TURN_ADDED',
              `Table voted to skip away player ${target.displayName}'s next turn.`,
              { targetSeatId: intent.targetSeatId }
            )
          );
        }
      }
      break;
    }

    case 'REMATCH_POD': {
      if (!hasAdminPower && Object.values(currentState.seats).filter((s) => s.accountId !== null).length > 1) {
        return { ok: false, error: 'Only the host or judge can launch a pod rematch.', state: currentState, emittedEvents: [] };
      }
      const seated = currentState.seatOrder.filter((sid) => currentState.seats[sid]?.accountId !== null);
      if (seated.length === 0) {
        return { ok: false, error: 'No players seated for rematch.', state: currentState, emittedEvents: [] };
      }
      let newSeatOrder = [...currentState.seatOrder];
      if (intent.reshuffleSeats !== false && seated.length > 1) {
        const shuffledSeated = shuffleArrayWithRng(seated, rng);
        const emptySeats = currentState.seatOrder.filter((sid) => !seated.includes(sid));
        newSeatOrder = [...shuffledSeated, ...emptySeats];
      }
      const startingSeatId = seated.includes(newSeatOrder[0]) ? newSeatOrder[0] : seated[rng.nextInt(seated.length)];
      const perSeatLibraryOrder: Record<string, string[]> = {};
      const perSeatOpeningHands: Record<string, string[]> = {};

      for (const sid of seated) {
        const owned = Object.values(currentState.cards).filter(
          (c) =>
            c.ownerSeatId === sid &&
            !c.isToken &&
            c.zone !== 'sideboard' &&
            !(c.isCommander && currentState.settings.useCommandZone)
        );
        const ordered = shuffleArrayWithRng(
          owned.map((c) => c.instanceId),
          rng
        );
        const handCount = Math.min(currentState.settings.openingHandSize, ordered.length);
        perSeatOpeningHands[sid] = ordered.slice(0, handCount);
        perSeatLibraryOrder[sid] = ordered;
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'POD_REMATCHED',
          `Rematch started! ${currentState.seats[startingSeatId]?.displayName ?? startingSeatId} leads the new game.`,
          { startingSeatId, newSeatOrder },
          { perSeatLibraryOrder, perSeatOpeningHands }
        )
      );
      break;
    }

    case 'UPDATE_RULE_ZERO': {
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'RULE_ZERO_UPDATED',
          `${actor.displayName} updated the Rule 0 checklist.`,
          { patch: intent.patch, actorSeatId: actor.seatId }
        )
      );
      break;
    }

    case 'AGREE_RULE_ZERO': {
      if (!seat) return { ok: false, error: 'Only seated players can agree to Rule 0.', state: currentState, emittedEvents: [] };
      const agreed = intent.agreed ?? true;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'RULE_ZERO_AGREED',
          `${actor.displayName} ${agreed ? 'agreed to' : 'unconfirmed'} the Rule 0 checklist.`,
          { seatId: seat.seatId, agreed }
        )
      );
      break;
    }

    case 'RUN_PREGAME_SEATING': {
      if (!hasAdminPower && Object.values(currentState.seats).filter((s) => s.accountId !== null).length > 1) {
        return { ok: false, error: 'Only the host can run pregame seating.', state: currentState, emittedEvents: [] };
      }
      const seated = currentState.seatOrder.filter((sid) => currentState.seats[sid]?.accountId !== null);
      if (seated.length === 0) {
        return { ok: false, error: 'At least one player must be seated.', state: currentState, emittedEvents: [] };
      }
      const emptySeats = currentState.seatOrder.filter((sid) => !seated.includes(sid));

      if (intent.mode === 'magical_chairs') {
        const shuffledSeated = shuffleArrayWithRng(seated, rng);
        const newSeatOrder = [...shuffledSeated, ...emptySeats];
        const startingSeatId = shuffledSeated[0];
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'PREGAME_SEATING_APPLIED',
            `Magical Chairs shuffled seats! ${currentState.seats[startingSeatId]?.displayName} sits first.`,
            { mode: 'magical_chairs', newSeatOrder, startingSeatId, highRollResults: {} }
          )
        );
      } else {
        // High-Roll d20 for each occupied seat, breaking top tie deterministically via CSPRNG
        const highRollResults: Record<string, number> = {};
        let bestRoll = -1;
        let winners: string[] = [];
        for (const sid of seated) {
          const r = rng.nextInt(20) + 1;
          highRollResults[sid] = r;
          if (r > bestRoll) {
            bestRoll = r;
            winners = [sid];
          } else if (r === bestRoll) {
            winners.push(sid);
          }
        }
        const startingSeatId = winners.length === 1 ? winners[0] : winners[rng.nextInt(winners.length)];
        if (winners.length > 1) {
          highRollResults[startingSeatId] = Math.min(20, bestRoll); // winner won tie-breaker
        }
        const startIdx = seated.indexOf(startingSeatId);
        const rotatedSeated = [...seated.slice(startIdx), ...seated.slice(0, startIdx)];
        const newSeatOrder = [...rotatedSeated, ...emptySeats];
        const summaryRolls = seated
          .map((sid) => `${currentState.seats[sid]?.displayName}: ${highRollResults[sid]}`)
          .join(', ');
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'PREGAME_SEATING_APPLIED',
            `High-Roll (${summaryRolls}) — ${currentState.seats[startingSeatId]?.displayName} goes first!`,
            { mode: 'high_roll', newSeatOrder, startingSeatId, highRollResults }
          )
        );
      }
      break;
    }

    case 'REVEAL_COMMANDERS_PREGAME': {
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PREGAME_COMMANDERS_REVEALED',
          `${actor.displayName} revealed pregame Commanders at the table!`,
          {}
        )
      );
      break;
    }

    case 'SET_STOP_PRESETS': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const validPhases = intent.phases.filter((ph) => GAME_PHASES.some((g) => g.id === ph));
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'STOP_PRESETS_UPDATED',
          `${actor.displayName} updated stop presets (${validPhases.length} phase stops).`,
          { seatId: seat.seatId, phases: validPhases }
        )
      );
      break;
    }

    case 'SET_FLAG_MUTE': {
      if (!hasAdminPower) {
        return { ok: false, error: 'Only the host or judge can mute flag spam.', state: currentState, emittedEvents: [] };
      }
      const target = currentState.seats[intent.targetSeatId];
      if (!target) return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'FLAG_MUTE_UPDATED',
          `${actor.displayName} ${intent.muted ? 'muted' : 'unmuted'} ${target.displayName}'s interaction flags.`,
          { targetSeatId: intent.targetSeatId, muted: intent.muted }
        )
      );
      break;
    }

    case 'GRANT_LIBRARY_ACCESS': {
      if (!seat) return { ok: false, error: 'Only a seated player can grant access to their library.', state: currentState, emittedEvents: [] };
      const grantee = currentState.seats[intent.granteeSeatId];
      if (!grantee || grantee.seatId === seat.seatId || grantee.status === 'eliminated' || grantee.accountId === null) {
        return { ok: false, error: 'Choose an active opponent seat to grant library access.', state: currentState, emittedEvents: [] };
      }
      const libIds = currentState.zoneOrder[seat.seatId].library;
      if (libIds.length === 0) {
        return { ok: false, error: 'Your library is empty.', state: currentState, emittedEvents: [] };
      }
      const sliceIds =
        intent.scope === 'entire'
          ? [...libIds]
          : libIds.slice(0, Math.max(1, Math.min(libIds.length, intent.count ?? 1)));

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'LIBRARY_ACCESS_GRANTED',
          `${actor.displayName} granted ${grantee.displayName} access to ${
            intent.scope === 'entire' ? 'their entire library' : `the top ${sliceIds.length} card(s)`
          }${intent.takeFaceDown ? ' (take face down)' : ''}.`,
          {
            ownerSeatId: seat.seatId,
            granteeSeatId: grantee.seatId,
            scope: intent.scope,
            count: sliceIds.length,
            takeFaceDown: intent.takeFaceDown,
          },
          { cardInstanceIds: sliceIds }
        )
      );
      break;
    }

    case 'TAKE_FROM_GRANTED_LIBRARY': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const owner = currentState.seats[intent.ownerSeatId];
      const grant = owner?.grantedLibraryAccess;
      if (!owner || !grant || grant.granteeSeatId !== seat.seatId) {
        return { ok: false, error: 'You do not have active library access for that player.', state: currentState, emittedEvents: [] };
      }
      if (!grant.cardInstanceIds.includes(intent.instanceId)) {
        return { ok: false, error: 'That card is outside your granted library slice.', state: currentState, emittedEvents: [] };
      }
      const card = currentState.cards[intent.instanceId];
      if (!card || card.zone !== 'library') {
        return { ok: false, error: 'Card is no longer in library.', state: currentState, emittedEvents: [] };
      }
      const destination = intent.destination ?? 'exile';
      const takeFaceDown = grant.takeFaceDown;
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'GRANTED_LIBRARY_CARD_TAKEN',
          takeFaceDown
            ? `${actor.displayName} took a face-down card from ${owner.displayName}'s library to ${destination}.`
            : `${actor.displayName} took ${card.card.name} from ${owner.displayName}'s library to ${destination}.`,
          {
            ownerSeatId: owner.seatId,
            granteeSeatId: seat.seatId,
            instanceId: card.instanceId,
            destination,
            takeFaceDown,
            publicCardName: takeFaceDown ? null : card.card.name,
            publicCardImage: takeFaceDown ? null : card.card.imageUrl,
          }
        )
      );
      break;
    }

    case 'REVOKE_LIBRARY_ACCESS': {
      const targetOwnerSid =
        intent.ownerSeatId ??
        (seat?.grantedLibraryAccess
          ? seat.seatId
          : Object.values(currentState.seats).find((s) => s.grantedLibraryAccess?.granteeSeatId === actor.seatId)
              ?.seatId);
      if (!targetOwnerSid) {
        return { ok: false, error: 'No active library access grant found.', state: currentState, emittedEvents: [] };
      }
      const owner = currentState.seats[targetOwnerSid];
      if (
        !owner?.grantedLibraryAccess ||
        (!hasAdminPower && actor.seatId !== owner.seatId && actor.seatId !== owner.grantedLibraryAccess.granteeSeatId)
      ) {
        return { ok: false, error: 'Only the library owner, grantee, or host can end library access.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'LIBRARY_ACCESS_REVOKED',
          `${actor.displayName} ended library access on ${owner.displayName}'s library.`,
          { ownerSeatId: owner.seatId }
        )
      );
      break;
    }

    case 'ADJUST_MANA_POOL': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const validColors: ManaPoolColor[] = ['W', 'U', 'B', 'R', 'G', 'C'];
      if (!validColors.includes(intent.color)) {
        return { ok: false, error: 'Invalid mana color.', state: currentState, emittedEvents: [] };
      }
      const nextAmount = Math.max(0, (seat.manaPool[intent.color] ?? 0) + intent.delta);
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'MANA_POOL_ADJUSTED',
          `${actor.displayName} floating {${intent.color}}: ${seat.manaPool[intent.color] ?? 0} → ${nextAmount}.`,
          { seatId: seat.seatId, color: intent.color, delta: intent.delta, amount: nextAmount }
        )
      );
      break;
    }

    case 'CLEAR_MANA_POOL': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'MANA_POOL_CLEARED',
          `${actor.displayName} emptied their floating mana pool.`,
          { seatId: seat.seatId }
        )
      );
      break;
    }

    case 'SET_RING_BEARER': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      let bearerName: string | null = null;
      if (intent.instanceId) {
        const perm = currentState.cards[intent.instanceId];
        if (!perm || perm.zone !== 'battlefield' || perm.controllerSeatId !== seat.seatId) {
          return {
            ok: false,
            error: 'Choose a permanent you control on the battlefield as your Ring-bearer.',
            state: currentState,
            emittedEvents: [],
          };
        }
        bearerName = perm.faceDown ? 'a face-down creature' : perm.card.name;
      }
      const prevTempt = currentState.designations.ringTemptCountBySeat[seat.seatId] ?? 0;
      const delta = intent.temptDelta ?? (intent.instanceId ? 1 : 0);
      const temptCount = Math.max(0, Math.min(4, prevTempt + delta));
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'RING_BEARER_SET',
          bearerName
            ? `The Ring tempts ${actor.displayName} (Level ${temptCount}/4) — Ring-bearer: ${bearerName}!`
            : `${actor.displayName} updated The Ring (Level ${temptCount}/4).`,
          { seatId: seat.seatId, instanceId: intent.instanceId, temptCount }
        )
      );
      break;
    }

    case 'SET_UNDERCITY_ROOM': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'UNDERCITY_ROOM_SET',
          intent.room
            ? `${actor.displayName} ventured into The Undercity: ${intent.room}!`
            : `${actor.displayName} completed / cleared their Undercity marker.`,
          { seatId: seat.seatId, room: intent.room }
        )
      );
      break;
    }

    case 'REVEAL_UNTIL': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const lib = currentState.zoneOrder[seat.seatId].library;
      if (lib.length === 0) {
        return { ok: false, error: 'Library is empty.', state: currentState, emittedEvents: [] };
      }

      const matchesCard = (def: CardDefinition): boolean => {
        if (intent.nonLand && /\bland\b/i.test(def.typeLine)) {
          return false;
        }
        if (intent.maxCmc !== undefined && def.cmc > intent.maxCmc) {
          return false;
        }
        if (intent.cardType && intent.cardType.trim()) {
          const needle = intent.cardType.trim().toLowerCase();
          if (!def.typeLine.toLowerCase().includes(needle)) {
            return false;
          }
        }
        return true;
      };

      const flippedIds: string[] = [];
      let hitId: string | null = null;
      for (const cid of lib) {
        const inst = currentState.cards[cid];
        if (!inst) continue;
        flippedIds.push(cid);
        if (matchesCard(inst.card)) {
          hitId = cid;
          break;
        }
      }

      const hitCard = hitId ? currentState.cards[hitId] : null;
      const privateOnly = Boolean(intent.privateOnly);
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'CARDS_REVEALED_UNTIL',
          privateOnly
            ? `${actor.displayName} privately searched from top (${flippedIds.length} card(s) inspected).`
            : hitCard
            ? `${actor.displayName} revealed ${flippedIds.length} card(s) until hitting ${hitCard.card.name}!`
            : `${actor.displayName} revealed all ${flippedIds.length} remaining library card(s) with no match.`,
          privateOnly
            ? { seatId: seat.seatId, privateOnly: true, count: flippedIds.length, matched: Boolean(hitId) }
            : {
                seatId: seat.seatId,
                privateOnly: false,
                count: flippedIds.length,
                flippedIds,
                hitInstanceId: hitId,
                hitCardName: hitCard?.card.name ?? null,
              },
          { flippedIds, hitInstanceId: hitId }
        )
      );
      break;
    }

    case 'RESOLVE_REVEALED_UNTIL': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const publicPool = currentState.sharedZones.revealed.filter(
        (cid) => currentState.cards[cid]?.ownerSeatId === seat.seatId
      );
      const isFromPrivateLook = publicPool.length === 0 && Boolean(seat.privateLook);
      const pool = isFromPrivateLook ? [...(seat.privateLook?.cardInstanceIds ?? [])] : publicPool;

      if (pool.length === 0) {
        return {
          ok: false,
          error: 'No revealed or searched cards waiting to be resolved.',
          state: currentState,
          emittedEvents: [],
        };
      }

      const hitId =
        intent.hitInstanceId && pool.includes(intent.hitInstanceId) ? intent.hitInstanceId : null;
      const restIds = pool.filter((cid) => cid !== hitId);
      const bottomedIds =
        intent.bottomOrder === 'current' ? restIds : shuffleArrayWithRng(restIds, rng);

      const hitCard = hitId ? currentState.cards[hitId] : null;
      const showHitName = Boolean(hitCard && (!isFromPrivateLook || intent.hitDestination !== 'hand'));
      const orderDesc = intent.bottomOrder === 'current' ? 'current order' : 'random order';

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'REVEALED_UNTIL_RESOLVED',
          hitCard
            ? `${actor.displayName} put ${showHitName ? hitCard.card.name : 'a card'} into ${intent.hitDestination} and placed ${bottomedIds.length} card(s) on the bottom of their library (${orderDesc}).`
            : `${actor.displayName} placed ${bottomedIds.length} card(s) on the bottom of their library (${orderDesc}).`,
          {
            seatId: seat.seatId,
            hitDestination: intent.hitDestination,
            bottomCount: bottomedIds.length,
            bottomOrder: intent.bottomOrder ?? 'random',
            publicHitCardName: showHitName ? hitCard!.card.name : null,
          },
          { hitInstanceId: hitId, bottomedIds }
        )
      );
      break;
    }

    case 'PICK_RANDOM_TARGET': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const activeSeats = getActiveSeatsInOrder(currentState);

      if (intent.scope === 'opponent' || intent.scope === 'any_player') {
        const candidates =
          intent.scope === 'any_player'
            ? activeSeats
            : activeSeats.filter((sid) => {
                if (sid === seat.seatId) return false;
                if (currentState.settings.teamMode && seat.teamId && currentState.seats[sid]?.teamId === seat.teamId) {
                  return false;
                }
                return true;
              });
        if (candidates.length === 0) {
          return { ok: false, error: 'No eligible player targets.', state: currentState, emittedEvents: [] };
        }
        const chosenSid = candidates[rng.nextInt(candidates.length)];
        const chosenName = currentState.seats[chosenSid]?.displayName ?? chosenSid;
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'RANDOM_TARGET_PICKED',
            `${actor.displayName} randomly picked ${intent.scope === 'opponent' ? 'opponent' : 'player'}: ${chosenName}!`,
            { scope: intent.scope, chosenSeatId: chosenSid, chosenLabel: chosenName }
          )
        );
      } else if (intent.scope === 'my_permanents' || intent.scope === 'all_permanents') {
        const permIds =
          intent.scope === 'my_permanents'
            ? [...currentState.zoneOrder[seat.seatId].battlefield]
            : activeSeats.flatMap((sid) => currentState.zoneOrder[sid]?.battlefield ?? []);
        if (permIds.length === 0) {
          return { ok: false, error: 'No permanents on the battlefield.', state: currentState, emittedEvents: [] };
        }
        const chosenId = permIds[rng.nextInt(permIds.length)];
        const perm = currentState.cards[chosenId];
        const label = perm?.faceDown ? 'a face-down permanent' : perm?.card.name ?? chosenId;
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'RANDOM_TARGET_PICKED',
            `${actor.displayName} randomly picked permanent: ${label}!`,
            { scope: intent.scope, chosenInstanceId: chosenId, chosenLabel: label }
          )
        );
      } else {
        // scope === 'my_hand': pick random slot # without leaking secret card names publicly!
        const handIds = currentState.zoneOrder[seat.seatId].hand;
        if (handIds.length === 0) {
          return { ok: false, error: 'Your hand is empty.', state: currentState, emittedEvents: [] };
        }
        const chosenIndex = rng.nextInt(handIds.length);
        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'RANDOM_TARGET_PICKED',
            `${actor.displayName} randomly selected card #${chosenIndex + 1} of ${handIds.length} in hand!`,
            { scope: 'my_hand', chosenHandIndex: chosenIndex, handCount: handIds.length }
          )
        );
      }
      break;
    }

    case 'UPDATE_PRIVATE_NOTES': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const cleanNotes = String(intent.notes ?? '').slice(0, 4000);
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PRIVATE_NOTES_UPDATED',
          `${actor.displayName} updated their private scratchpad.`,
          { seatId: seat.seatId },
          { notes: cleanNotes }
        )
      );
      break;
    }

    case 'TOGGLE_SAVE_FOR_LATER': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const cardName = String(intent.cardName ?? '').trim().slice(0, 120);
      if (!cardName) {
        return { ok: false, error: 'Card name required.', state: currentState, emittedEvents: [] };
      }
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'SAVED_FOR_LATER_TOGGLED',
          `${actor.displayName} updated their playtest tray.`,
          { seatId: seat.seatId },
          { cardName }
        )
      );
      break;
    }

    case 'CONFIGURE_PAPER_MODE': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const nextConsent = intent.consentAccepted ?? seat.paperMode.consentAccepted;
      const nextCameraMode = intent.cameraMode ?? seat.paperMode.cameraMode;
      if (nextCameraMode !== 'off' && !nextConsent) {
        return {
          ok: false,
          error: 'Explicit camera privacy consent is required before activating a webcam or companion phone camera.',
          state: currentState,
          emittedEvents: [],
        };
      }

      const clampCrop = (v: number | undefined, fallback: number) =>
        Math.max(0, Math.min(40, Number.isFinite(v) ? Number(v) : fallback));

      const nextCrop = intent.tableOnlyCrop
        ? {
            top: clampCrop(intent.tableOnlyCrop.top, seat.paperMode.tableOnlyCrop.top),
            right: clampCrop(intent.tableOnlyCrop.right, seat.paperMode.tableOnlyCrop.right),
            bottom: clampCrop(intent.tableOnlyCrop.bottom, seat.paperMode.tableOnlyCrop.bottom),
            left: clampCrop(intent.tableOnlyCrop.left, seat.paperMode.tableOnlyCrop.left),
          }
        : { ...seat.paperMode.tableOnlyCrop };

      const patch: Partial<PaperSeatConfig> = {
        enabled: intent.enabled ?? seat.paperMode.enabled,
        cameraMode: nextCameraMode,
        consentAccepted: nextConsent,
        aspectRatio: intent.aspectRatio === '4:3' ? '4:3' : intent.aspectRatio === '16:9' ? '16:9' : seat.paperMode.aspectRatio,
        preferredCodec:
          intent.preferredCodec === 'vp8' || intent.preferredCodec === 'h264' || intent.preferredCodec === 'vp9'
            ? intent.preferredCodec
            : seat.paperMode.preferredCodec,
        lowBandwidthFps:
          intent.lowBandwidthFps !== undefined
            ? Math.max(0.2, Math.min(5, Number(intent.lowBandwidthFps) || 1))
            : seat.paperMode.lowBandwidthFps,
        tableOnlyCrop: nextCrop,
        privacyBlur: intent.privacyBlur ?? seat.paperMode.privacyBlur,
      };

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PAPER_MODE_CONFIGURED',
          `${actor.displayName} ${patch.enabled ? 'enabled Paper & Webcam Mode' : 'switched to Digital Mode'}.`,
          { seatId: seat.seatId, patch }
        )
      );
      break;
    }

    case 'CONFIRM_PAPER_SCAN': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      if (!seat.paperMode.enabled) {
        return {
          ok: false,
          error: 'Enable Paper & Webcam Mode on your seat before confirming physical card scans.',
          state: currentState,
          emittedEvents: [],
        };
      }

      const rawName = String(intent.cardName ?? '').trim();
      if (!rawName) {
        return { ok: false, error: 'Scanned card name is required.', state: currentState, emittedEvents: [] };
      }

      const toZone = intent.toZone ?? 'battlefield';
      const lowerName = rawName.toLowerCase();

      // Check seat's owned deck cards + available tokens (strictly deck-limited!)
      const ownedCards = Object.values(currentState.cards).filter((c) => c.ownerSeatId === seat.seatId && !c.isToken);
      const matchingDeckCards = ownedCards.filter((c) => c.card.name.toLowerCase() === lowerName);
      const matchingToken = seat.availableTokens.find((t) => t.name.toLowerCase() === lowerName);

      if (matchingDeckCards.length === 0 && !matchingToken) {
        return {
          ok: false,
          error: `"${rawName}" is not in your loaded deck or token pool. Use Off-List Paper Card Update if playing it physically.`,
          state: currentState,
          emittedEvents: [],
        };
      }

      const registeredAlter = seat.paperMode.registeredAlters.find(
        (a) => a.cardName.toLowerCase() === lowerName
      );

      // 1-tap digital token copies (`asTokenCopy` or token template match)
      if (intent.asTokenCopy || (!matchingDeckCards.length && matchingToken)) {
        const count = Math.max(1, Math.min(20, Math.floor(intent.count ?? 1)));
        const sourceCard: CardDefinition = matchingDeckCards[0]?.card ?? {
          scryfallId: `token_${matchingToken!.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
          oracleId: `oracle_tok_${matchingToken!.name.toLowerCase()}`,
          name: matchingToken!.name,
          manaCost: '',
          cmc: 0,
          typeLine: matchingToken!.typeLine,
          oracleText: matchingToken!.oracleText,
          colors: matchingToken!.colors,
          colorIdentity: matchingToken!.colors,
          power: matchingToken!.power,
          toughness: matchingToken!.toughness,
          imageUrl: matchingToken!.imageUrl,
          artCropUrl: matchingToken!.imageUrl,
          isGameChanger: false,
        };

        const spawnedTokens: CardInstance[] = [];
        for (let i = 0; i < count; i++) {
          spawnedTokens.push({
            instanceId: `ptok_${seat.seatId}_${nextSeq}_${i + 1}`,
            ownerSeatId: seat.seatId,
            controllerSeatId: seat.seatId,
            card: structuredClone(sourceCard),
            zone: 'battlefield',
            tapped: false,
            faceDown: false,
            flipped: false,
            counters: {},
            attachedToId: null,
            exiledByInstanceId: null,
            row: classifyBattlefieldRow(sourceCard.typeLine),
            freePos: null,
            peekedBySeatIds: [],
            isToken: true,
            isCommander: false,
            enteredZoneSeq: nextSeq,
          });
        }

        const scanEntry = {
          scanId: `scan_${nextSeq}`,
          cardName: sourceCard.name,
          scryfallId: sourceCard.scryfallId,
          artistCredit: registeredAlter?.artistCredit ?? null,
          zone: 'battlefield' as const,
          timestamp: now,
        };

        events.push(
          makeEvent(
            currentState,
            nextSeq++,
            now,
            actor,
            'PAPER_CARD_SCANNED',
            `${actor.displayName} scanned ${count}× ${sourceCard.name} token(s) onto their Digital Twin Board.`,
            { seatId: seat.seatId, toZone: 'battlefield', scanEntry, tokenCount: count },
            { movedInstanceId: null, spawnedTokens }
          )
        );
        break;
      }

      // Prefer moving an unrevealed copy from library -> hand -> command -> sideboard so physical play syncs cleanly
      const zonePriority: Record<string, number> = {
        library: 1,
        hand: 2,
        command: 3,
        sideboard: 4,
        graveyard: 5,
        exile: 6,
        battlefield: 7,
      };
      const sortedCopies = [...matchingDeckCards].sort(
        (a, b) => (zonePriority[a.zone] ?? 9) - (zonePriority[b.zone] ?? 9)
      );
      const chosen = sortedCopies[0];

      const scanEntry = {
        scanId: `scan_${nextSeq}`,
        cardName: chosen.card.name,
        scryfallId: chosen.card.scryfallId,
        artistCredit: registeredAlter?.artistCredit ?? null,
        zone: toZone,
        timestamp: now,
      };

      const creditSuffix = registeredAlter ? ` (Alter art by ${registeredAlter.artistCredit})` : '';
      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PAPER_CARD_SCANNED',
          `${actor.displayName} scanned ${chosen.card.name}${creditSuffix} to ${toZone}.`,
          {
            seatId: seat.seatId,
            toZone,
            scanEntry,
            publicCardName: chosen.card.name,
            publicCardImage: chosen.card.imageUrl,
          },
          { movedInstanceId: chosen.instanceId, spawnedTokens: [] }
        )
      );
      break;
    }

    case 'ADD_PAPER_CARD_LIVE': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      if (!seat.paperMode.enabled) {
        return {
          ok: false,
          error: 'Live mid-game deck updates are strictly restricted to Paper & Webcam Mode seats.',
          state: currentState,
          emittedEvents: [],
        };
      }
      if (!intent.card || !intent.card.name?.trim()) {
        return { ok: false, error: 'Valid Scryfall card definition required.', state: currentState, emittedEvents: [] };
      }

      const toZone = intent.toZone ?? 'battlefield';
      const isPublicZone = toZone === 'battlefield' || toZone === 'command';
      const newInst: CardInstance = {
        instanceId: `ppaper_${seat.seatId}_${nextSeq}`,
        ownerSeatId: seat.seatId,
        controllerSeatId: seat.seatId,
        card: structuredClone(intent.card),
        zone: toZone,
        tapped: false,
        faceDown: false,
        flipped: false,
        counters: {},
        attachedToId: null,
        exiledByInstanceId: null,
        row: classifyBattlefieldRow(intent.card.typeLine),
        freePos: null,
        peekedBySeatIds: [],
        isToken: false,
        isCommander: toZone === 'command',
        isTemporaryPaperAdd: intent.scope === 'this_game_only',
        enteredZoneSeq: nextSeq,
      };

      const scanEntry = isPublicZone
        ? {
            scanId: `scan_${nextSeq}`,
            cardName: intent.card.name,
            scryfallId: intent.card.scryfallId,
            artistCredit: null,
            zone: toZone as 'battlefield' | 'command',
            timestamp: now,
          }
        : null;

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PAPER_CARD_ADDED_LIVE',
          isPublicZone
            ? `${actor.displayName} resolved off-list paper card ${intent.card.name} (${
                intent.scope === 'save_deck_update' ? 'saved to deck' : 'this game only'
              }) onto ${toZone}.`
            : `${actor.displayName} registered an off-list paper card (${
                intent.scope === 'save_deck_update' ? 'saved to deck' : 'this game only'
              }).`,
          {
            seatId: seat.seatId,
            scope: intent.scope,
            toZone,
            scanEntry,
          },
          { cardInstance: newInst }
        )
      );
      break;
    }

    case 'SET_VIDEO_MARKER': {
      const targetSid = intent.targetSeatId ?? seat?.seatId;
      const targetSeat = targetSid ? currentState.seats[targetSid] : null;
      if (!targetSeat) {
        return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      }
      if (!hasAdminPower && seat?.seatId !== targetSeat.seatId) {
        return {
          ok: false,
          error: 'Only the seat owner, host, or judge can place markers on that paper feed.',
          state: currentState,
          emittedEvents: [],
        };
      }
      const label = String(intent.label ?? '').trim().slice(0, 48);
      if (!label) {
        return { ok: false, error: 'Video marker label is required.', state: currentState, emittedEvents: [] };
      }

      const marker: PaperVideoMarker = {
        markerId: intent.markerId?.trim() || `vmk_${nextSeq}`,
        label,
        kind: intent.kind,
        xPct: Math.max(2, Math.min(98, Number(intent.xPct) || 50)),
        yPct: Math.max(2, Math.min(98, Number(intent.yPct) || 50)),
        count: intent.count !== undefined ? Math.max(0, Math.min(999, Math.floor(intent.count))) : undefined,
      };

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'VIDEO_MARKER_SET',
          `${actor.displayName} pinned marker "${marker.label}" on ${targetSeat.displayName}'s playmat.`,
          { targetSeatId: targetSeat.seatId, marker }
        )
      );
      break;
    }

    case 'CLEAR_VIDEO_MARKER': {
      const targetSid = intent.targetSeatId ?? seat?.seatId;
      const targetSeat = targetSid ? currentState.seats[targetSid] : null;
      if (!targetSeat) {
        return { ok: false, error: 'Target seat not found.', state: currentState, emittedEvents: [] };
      }
      if (!hasAdminPower && seat?.seatId !== targetSeat.seatId) {
        return {
          ok: false,
          error: 'Only the seat owner, host, or judge can clear markers on that paper feed.',
          state: currentState,
          emittedEvents: [],
        };
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'VIDEO_MARKER_CLEARED',
          `${actor.displayName} cleared ${intent.markerId === 'all' ? 'all video markers' : 'a video marker'} on ${targetSeat.displayName}'s playmat.`,
          { targetSeatId: targetSeat.seatId, markerId: intent.markerId }
        )
      );
      break;
    }

    case 'REGISTER_PAPER_ALTER': {
      if (!seat) return { ok: false, error: 'Not seated.', state: currentState, emittedEvents: [] };
      const deckCardNames = Array.from(
        new Set(
          Object.values(currentState.cards)
            .filter((c) => c.ownerSeatId === seat.seatId && !c.isToken)
            .map((c) => c.card.name)
        )
      );
      const validation = validateAlterRegistration({
        cardName: intent.cardName ?? '',
        artistCredit: intent.artistCredit ?? '',
        dHashHex: intent.dHashHex ?? '',
        deckCardNames,
      });
      if (!validation.ok) {
        return {
          ok: false,
          error: validation.error,
          state: currentState,
          emittedEvents: [],
        };
      }

      events.push(
        makeEvent(
          currentState,
          nextSeq++,
          now,
          actor,
          'PAPER_ALTER_REGISTERED',
          `${actor.displayName} registered a custom alter hash in their paper deck.`,
          { seatId: seat.seatId },
          {
            alter: {
              cardName: validation.canonicalCardName,
              artistCredit: validation.artistCredit,
              dHashHex: validation.dHashHex,
              registeredAt: now,
            },
          }
        )
      );
      break;
    }

    default:
      return { ok: false, error: 'Unsupported intent.', state: currentState, emittedEvents: [] };
  }

  let nextState = currentState;
  for (const ev of events) {
    nextState = applyEvent(nextState, ev);
  }
  return {
    ok: true,
    state: nextState,
    emittedEvents: events,
  };
}
